import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import './App.css';
import { PROFESSION_CATEGORIES, DIFFICULTY_LEVELS, PE_POINTS_OPTIONS, LANGUAGES, API_BASE_URL as API_CONFIG_BASE_URL } from './constants';
import { PE_POINTS_VALUES } from './constants';

// API Configuration
const API_BASE_URL = API_CONFIG_BASE_URL + '/api';

// Main App with Router
function App() {
  // Authentication state at App level
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      checkAuthStatus(token);
    } else {
      setAuthLoading(false);
    }
  }, []);

  const checkAuthStatus = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        if (userData.is_admin) {
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('admin_token');
          // Don't show alert, just redirect to login
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        localStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('admin_token');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.user.is_admin) {
          localStorage.setItem('admin_token', data.access_token);
          setUser(data.user);
          setIsAuthenticated(true);
          return { success: true };
        } else {
          return { success: false, error: 'Admin access required' };
        }
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setUser(null);
  };

  // Helper function to handle API authentication failures
  const handleApiAuthFailure = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setUser(null);
  };

  const LoginForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');
      setIsLoading(true);

      const result = await handleLogin(email, password);
      
      if (!result.success) {
        setError(result.error);
      }
      
      setIsLoading(false);
    };

    return (
      <div className="auth-container">
        <div className="auth-form">
          <h2>Admin Login</h2>
          <p className="auth-subtitle">Access the Protagon Admin Dashboard</p>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Admin Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login to Admin Panel'}
            </button>
          </form>
        </div>
      </div>
    );
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading">Checking admin authentication...</div>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="App">
        <nav className="navbar">
          <div className="nav-brand">
            <h2>Protagon - Admin Dashboard</h2>
          </div>
          <div className="nav-info">
            <span>Admin access required</span>
          </div>
        </nav>
        <main className="main-content">
          <LoginForm />
        </main>
      </div>
    );
  }

  // If authenticated, show the admin dashboard with routing
  return (
    <Router>
      <Routes>
        <Route path="/" element={<DashboardPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/dashboard" element={<DashboardPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/external-links" element={<DashboardPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/youtube-videos" element={<DashboardPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/podcasts" element={<DashboardPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/add-article" element={<AddArticlePage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/edit-article/:id" element={<EditArticlePage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/news-management" element={<NewsManagementPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/feedback-management" element={<FeedbackManagementPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/discount-codes" element={<DiscountCodesPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
        <Route path="/article-requests" element={<ArticleRequestsPage user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />} />
      </Routes>
    </Router>
  );
}

// Main Admin Dashboard Component
function AdminDashboard({ user, handleLogout, handleApiAuthFailure }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedSource, setSelectedSource] = useState('All');
  const [selectedLanguage, setSelectedLanguage] = useState('All');
  const [selectedType, setSelectedType] = useState('All'); // All | YouTube | Non-YouTube
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    summary: '',
    author: '',
    source: '',
    category: '',
    secondary_category: '',
    subcategory: '',
    language: 'English',
    pe_points_value: 0.25,
    difficulty_level: 'Intermediate',
    estimated_read_time: 10,
    is_published: true,
    publication_date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  });

  // PDF Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);
  const [processingMode, setProcessingMode] = useState('manual'); // 'manual' or 'pdf' or 'external'
  const [manualTextProcessing, setManualTextProcessing] = useState(false);
  const [pdfFilePath, setPdfFilePath] = useState(''); // Store PDF file path (deprecated)
  const [pdfContent, setPdfContent] = useState(''); // Store PDF content as hex string
  const [pdfFilename, setPdfFilename] = useState(''); // Store PDF filename
  const [externalUrl, setExternalUrl] = useState(''); // Store external URL

  // Question editing state
  const [editingQuestions, setEditingQuestions] = useState({}); // Track which questions are being edited
  const [editingQuestionData, setEditingQuestionData] = useState({}); // Store temporary editing data

  // New state for archive toggle
  const [showArchived, setShowArchived] = useState(false);

  // Define functions before useEffect hooks that use them
  const normalizeSource = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const safeText = (v) => (v === null || v === undefined) ? '' : String(v);

  const filterArticles = () => {
    let filtered = [...articles];

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Filter by status
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(article => {
        if (selectedStatus === 'Published') return article.is_published && !article.archived;
        if (selectedStatus === 'Draft') return !article.is_published && !article.archived;
        if (selectedStatus === 'Archived') return article.archived;
        return true;
      });
    }

    // Filter by archive visibility
    if (!showArchived) {
      filtered = filtered.filter(article => !article.archived);
    }

    // Filter by source (use normalized comparison)
    if (selectedSource !== 'All') {
      filtered = filtered.filter(article => normalizeSource(article.source) === selectedSource);
    }

    // Filter by language
    if (selectedLanguage !== 'All') {
      filtered = filtered.filter(article => article.language === selectedLanguage);
    }

    // Filter by type (YouTube vs Non-YouTube)
    if (selectedType !== 'All') {
      const isYouTube = (url) => {
        if (!url) return false;
        try {
          const u = new URL(url);
          const host = u.hostname.replace('www.', '');
          if (host === 'youtube.com' || host === 'youtu.be') return true;
          return u.pathname.includes('/embed/');
        } catch { return false; }
      };
      if (selectedType === 'YouTube') {
        filtered = filtered.filter(a => isYouTube(a.external_url));
      } else if (selectedType === 'Non-YouTube') {
        filtered = filtered.filter(a => !isYouTube(a.external_url));
      }
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = safeText(searchTerm).toLowerCase();
      filtered = filtered.filter(article => {
        const title = safeText(article.title).toLowerCase();
        const author = safeText(article.author).toLowerCase();
        const source = safeText(article.source).toLowerCase();
        const summary = safeText(article.summary).toLowerCase();
        const content = safeText(article.content).toLowerCase();
        return (
          title.includes(searchLower) ||
          author.includes(searchLower) ||
          source.includes(searchLower) ||
          summary.includes(searchLower) ||
          content.includes(searchLower)
        );
      });
    }

    setFilteredArticles(filtered);
  };

  // Fetch articles from backend
  const fetchArticles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }
      
      const includeArchived = showArchived ? 'true' : 'false';
      const response = await fetch(`${API_BASE_URL}/admin/articles?include_archived=${includeArchived}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      } else if (response.status === 401 || response.status === 403) {
        // Token expired or invalid
        handleApiAuthFailure();
      } else {
        console.error('Error fetching articles:', response.status);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter articles when articles, category, status, source, language, or search term changes
  useEffect(() => {
    filterArticles();
  }, [articles, selectedCategory, selectedStatus, selectedSource, selectedLanguage, selectedType, searchTerm, showArchived]);

  useEffect(() => {
    fetchArticles();
  }, [showArchived]);

  // Load article for editing when on edit route
  useEffect(() => {
    if (location.pathname.startsWith('/edit-article/') && params.id) {
      const articleId = parseInt(params.id);
      const article = articles.find(a => a.id === articleId);
      if (article && (!editingArticle || editingArticle.id !== articleId)) {
        // Only set the editing state, don't navigate again
        setEditingArticle(article);
        
        // Load article data into the form
        setNewArticle({
          title: article.title,
          content: article.content,
          summary: article.summary,
          author: article.author,
          source: article.source,
          category: article.category,
          secondary_category: article.secondary_category || '',
          language: article.language,
          pe_points_value: article.pe_points_value,
          difficulty_level: article.difficulty_level,
          estimated_read_time: article.estimated_read_time,
          is_published: article.is_published,
          publication_date: article.publication_date ? new Date(article.publication_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        });

        // If this is a YouTube article, prefill the YouTube editor state
        try {
          const url = article.external_url || '';
          const isYT = url.includes('youtube.com') || url.includes('youtu.be');
          if (isYT) {
            setYtTitle(article.title || '');
            setYtSummary(article.summary || '');
            setYtCategory(article.category || '');
            setYtSecondaryCategory(article.secondary_category || '');
            setYtLanguage(article.language || 'English');
            setYtPePoints(article.pe_points_value || 0.25);
            setYtDifficulty(article.difficulty_level || 'Intermediate');
            if (typeof article.estimated_read_time === 'number') {
              setYtWatchTime(article.estimated_read_time);
            }
            try { setYtVideoId(extractYouTubeId(url)); } catch {}
          }
        } catch {}
        
        // Load existing questions
        const loadQuestions = async () => {
          try {
            const token = localStorage.getItem('admin_token');
            if (!token) {
              handleApiAuthFailure();
              return;
            }
            
            const questionsResponse = await fetch(`${API_BASE_URL}/articles/${article.id}/tests`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (questionsResponse.ok) {
              const questions = await questionsResponse.json();
              setGeneratedQuestions(questions);
              setShowQuestions(questions.length > 0);
              // Prefill YT questions if this is a YouTube article
              try {
                const url = article.external_url || '';
                const isYT = url.includes('youtube.com') || url.includes('youtu.be');
                if (isYT) {
                  const mapped = (questions || []).map((q) => ({
                    prompt: q.question || q.prompt || '',
                    option_a: q.option_a || q.options?.A || '',
                    option_b: q.option_b || q.options?.B || '',
                    option_c: q.option_c || q.options?.C || '',
                    option_d: q.option_d || q.options?.D || '',
                    correct: q.correct_answer || q.correct || 'A',
                    explanation: q.explanation || ''
                  }));
                  setYtQuestions(mapped);
                }
              } catch {}
            } else if (questionsResponse.status === 401 || questionsResponse.status === 403) {
              // Token expired or invalid
              handleApiAuthFailure();
            }
          } catch (error) {
            console.error('Error loading questions:', error);
          }
        };
        
        loadQuestions();
        
        // Set PDF file path if exists
        if (article.pdf_file_path) {
          setPdfFilePath(article.pdf_file_path);
          setProcessingMode('pdf');
        } else if (article.external_url) {
          setExternalUrl(article.external_url);
          setProcessingMode('external');
        } else {
          setProcessingMode('manual');
        }
      }
    } else if (!location.pathname.startsWith('/edit-article/') && editingArticle) {
      // Clear editing state when not on edit route
      setEditingArticle(null);
    }
  }, [location.pathname, params.id, articles, editingArticle]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let nextValue = type === 'checkbox' ? checked : value;
    if (name === 'pe_points_value') {
      // Normalize decimal separator and coerce to number with two decimals
      const normalized = String(value).replace(',', '.');
      const parsed = parseFloat(normalized);
      nextValue = isNaN(parsed) ? '' : parsed;
    }
    setNewArticle(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setUploadSuccess(false);
    } else {
      alert('Please select a PDF file');
      e.target.value = '';
    }
  };

  // Handle PDF upload and text extraction
  const handlePdfUpload = async () => {
    if (!selectedFile) {
      alert('Please select a PDF file first');
      return;
    }

    // Validate that language and category are selected
    if (!newArticle.language) {
      alert('Please select a language before uploading PDF');
      return;
    }

    if (!newArticle.category) {
      alert('Please select a category before uploading PDF');
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('language', newArticle.language);
      formData.append('category', newArticle.category);

      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/upload-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        
        // Auto-fill the form with extracted data
        setNewArticle(prev => ({
          ...prev,
          // Don't set content for PDFs - leave empty so only iframe shows
          summary: data.suggested_summary,
          // Only set estimated_read_time if user hasn't manually changed it from default
          estimated_read_time: prev.estimated_read_time === 10 ? data.estimated_read_time : prev.estimated_read_time,
          // Use AI-generated title instead of filename
          title: data.suggested_title || prev.title || selectedFile.name.replace('.pdf', '').replace(/[_-]/g, ' ')
        }));

        // Store generated questions
        setGeneratedQuestions(data.generated_questions || []);
        setShowQuestions(true);
        setUploadSuccess(true);
        setPdfFilePath(data.pdf_file_path);
        setPdfContent(data.pdf_content);
        setPdfFilename(data.filename);
        
        // Enhanced success message with processing details
        const extractionMethod = data.extraction_method || 'unknown';
        const ocrEnhanced = data.processing_details?.ocr_enhanced || false;
        
        let successMessage = `📄 PDF processed successfully!\n\n`;
        successMessage += `📊 Document Stats:\n`;
        successMessage += `• Word count: ${data.word_count}\n`;
        successMessage += `• Estimated read time: ${data.estimated_read_time} minutes\n`;
        successMessage += `• Generated questions: ${data.questions_count}\n`;
        successMessage += `• Category: ${data.category}\n`;
        successMessage += `• Language: ${data.language}\n\n`;
        successMessage += `🔧 Processing Details:\n`;
        successMessage += `• Extraction method: ${extractionMethod === 'text-based' ? '📝 Text-based PDF' : '🔍 OCR (Scanned PDF)'}\n`;
        successMessage += `• Text quality: ${data.text_quality || 'Validated'}\n`;
        
        if (ocrEnhanced) {
          successMessage += `• 🚀 Enhanced OCR processing applied for better accuracy`;
        }
        
        alert(successMessage);
      } else {
        const errorData = await response.json();
        
        // Enhanced error message with processing details if available
        let errorMessage = `❌ Error processing PDF: ${errorData.error}`;
        
        if (errorData.extraction_method) {
          errorMessage += `\n\n🔧 Processing Details:\n`;
          errorMessage += `• Attempted method: ${errorData.extraction_method}\n`;
          
          if (errorData.text_sample) {
            errorMessage += `• Text sample: ${errorData.text_sample.substring(0, 100)}...`;
          }
        }
        
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Error uploading PDF');
    } finally {
      setUploadLoading(false);
    }
  };

  // Process manual text input with AI
  const handleProcessManualText = async () => {
    if (!newArticle.content.trim()) {
      alert('Please enter some article content first');
      return;
    }

    // Validate that language and category are selected
    if (!newArticle.language) {
      alert('Please select a language before processing text');
      return;
    }

    if (!newArticle.category) {
      alert('Please select a category before processing text');
      return;
    }

    setManualTextProcessing(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Send the content, language, and category to generate questions
      const response = await fetch(`${API_BASE_URL}/process-manual-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newArticle.content,
          language: newArticle.language,
          category: newArticle.category
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update form with AI enhancements
        setNewArticle(prev => ({
          ...prev,
          content: data.formatted_content,
          summary: data.suggested_summary,
          estimated_read_time: data.estimated_read_time
        }));

        // Store generated questions
        setGeneratedQuestions(data.generated_questions || []);
        setShowQuestions(true);
        alert(`Text processed successfully!\nWord count: ${data.word_count}\nEstimated read time: ${data.estimated_read_time} minutes\nGenerated questions: ${data.questions_count}\nCategory: ${data.category}\nLanguage: ${data.language}`);
      } else {
        const errorData = await response.json();
        alert(`Error processing text: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error processing manual text:', error);
      alert('Error processing text');
    } finally {
      setManualTextProcessing(false);
    }
  };

  // Handle external link processing
  const handleProcessExternalLink = async () => {
    if (!externalUrl.trim()) {
      alert('Please enter a valid URL first');
      return;
    }

    if (!newArticle.content.trim()) {
      alert('Please paste the article content in the Content field below first');
      return;
    }

    // Validate that language and category are selected
    if (!newArticle.language) {
      alert('Please select a language before processing external link');
      return;
    }

    if (!newArticle.category) {
      alert('Please select a category before processing external link');
      return;
    }

    setManualTextProcessing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/process-external-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url: externalUrl,
          content: newArticle.content,
          language: newArticle.language,
          category: newArticle.category
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Auto-fill the form with AI-generated data
        setNewArticle(prev => ({
          ...prev,
          summary: data.suggested_summary,
          title: data.suggested_title || prev.title,
          // Only set estimated_read_time if user hasn't manually changed it from default
          estimated_read_time: prev.estimated_read_time === 10 ? data.estimated_read_time : prev.estimated_read_time
          // Keep the original content as it was manually pasted
        }));

        // Store generated questions
        setGeneratedQuestions(data.generated_questions || []);
        setShowQuestions(true);
        setUploadSuccess(true);
        alert(`External link processed successfully!\nWord count: ${data.word_count}\nEstimated read time: ${data.estimated_read_time} minutes\nGenerated questions: ${data.questions_count}\nCategory: ${data.category}\nLanguage: ${data.language}`);
      } else {
        const errorData = await response.json();
        alert(`Error processing external link: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error processing external link:', error);
      alert('Error processing external link');
    } finally {
      setManualTextProcessing(false);
    }
  };

  // Create article with generated questions
  const handleCreateArticleWithQuestions = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const articleData = {
        ...newArticle,
        questions: generatedQuestions,
        pdf_content_hex: processingMode === 'pdf' ? pdfContent : null,
        pdf_filename: processingMode === 'pdf' ? pdfFilename : null,
        external_url: processingMode === 'external' ? externalUrl : null,
      };

      const response = await fetch(`${API_BASE_URL}/create-article-with-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(articleData),
      });

      if (response.ok) {
        const data = await response.json();
        navigate('/dashboard');
        setNewArticle({
          title: '',
          content: '',
          summary: '',
          author: '',
          source: '',
          category: '',
          language: 'English',
          pe_points_value: 0.25,
          difficulty_level: 'Intermediate',
          estimated_read_time: 10,
          is_published: true,
          publication_date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
        });
        // Reset PDF upload state
        setSelectedFile(null);
        setUploadSuccess(false);
        setGeneratedQuestions([]);
        setShowQuestions(false);
        setProcessingMode('manual');
        setManualTextProcessing(false);
        setPdfFilePath('');
        setPdfContent('');
        setPdfFilename('');
        setExternalUrl(''); // Clear external URL
        fetchArticles(); // Refresh the list
        alert(`${data.message}`);
      } else {
        const errorData = await response.json();
        alert(`Error creating article: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error creating article with questions:', error);
      alert('Error creating article with questions');
    }
  };

  const handleCancelForm = () => {
    navigate(-1); // Go back to previous page
    setEditingArticle(null);
    setNewArticle({
      title: '',
      content: '',
      summary: '',
      author: '',
      source: '',
      category: '',
      language: 'English',
      pe_points_value: 0.25,
      difficulty_level: 'Intermediate',
      estimated_read_time: 10,
      is_published: true,
      publication_date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
    });
    setSelectedFile(null);
    setUploadSuccess(false);
    setGeneratedQuestions([]);
    setShowQuestions(false);
    setProcessingMode('manual');
    setManualTextProcessing(false);
    setPdfFilePath('');
    setPdfContent('');
    setPdfFilename('');
    setExternalUrl(''); // Clear external URL
    // Clear question editing state
    setEditingQuestions({});
    setEditingQuestionData({});
  };

  // Archive/Unarchive article
  const handleArchiveArticle = async (articleId, archived) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/articles/${articleId}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ archived: !archived }),
      });

      if (response.ok) {
        // Refresh articles list
        fetchArticles();
        alert(`Article ${!archived ? 'archived' : 'unarchived'} successfully`);
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error archiving article:', error);
      alert('Error archiving article');
    }
  };

  // Delete article
  const handleDeleteArticle = async (articleId) => {
    if (window.confirm('Are you sure you want to delete this article? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${API_BASE_URL}/articles/${articleId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          // Refresh articles list
          fetchArticles();
          alert('Article deleted successfully');
        } else {
          const errorData = await response.json();
          alert(`Error: ${errorData.error}`);
        }
      } catch (error) {
        console.error('Error deleting article:', error);
        alert('Error deleting article');
      }
    }
  };

  // Update existing article
  const handleUpdateArticle = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      
      // Prepare article data
      const articleData = {
        ...newArticle,
        pdf_file_path: pdfFilePath || null,
        external_url: externalUrl || null,
      };
      
      // Update article
      const articleResponse = await fetch(`${API_BASE_URL}/articles/${editingArticle.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(articleData),
      });

      if (articleResponse.ok) {
        // Update questions if any
        if (generatedQuestions.length > 0) {
          const questionsResponse = await fetch(`${API_BASE_URL}/articles/${editingArticle.id}/questions`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ questions: generatedQuestions }),
          });
          
          if (!questionsResponse.ok) {
            const questionError = await questionsResponse.json();
            alert(`Article updated but error updating questions: ${questionError.error}`);
          }
        }
        
        alert('Article updated successfully!');
        navigate(-1); // Go back to previous page
        fetchArticles();
      } else {
        const errorData = await articleResponse.json();
        alert(`Error updating article: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error updating article:', error);
      alert('Error updating article');
    }
  };

  // Question editing functions
  const startEditingQuestion = (index) => {
    const question = generatedQuestions[index];
    setEditingQuestions(prev => ({ ...prev, [index]: true }));
    
    // Convert from nested options structure to flat structure for editing
    const editingData = {
      question: question.question || '',
      option_a: question.options?.A || question.option_a || '',
      option_b: question.options?.B || question.option_b || '',
      option_c: question.options?.C || question.option_c || '',
      option_d: question.options?.D || question.option_d || '',
      correct_answer: question.correct_answer || 'A',
      explanation: question.explanation || ''
    };
    
    setEditingQuestionData(prev => ({ ...prev, [index]: editingData }));
  };

  const cancelEditingQuestion = (index) => {
    setEditingQuestions(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
    setEditingQuestionData(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  const saveEditedQuestion = (index) => {
    const editedQuestion = editingQuestionData[index];
    
    // Validate question data
    if (!editedQuestion.question.trim()) {
      alert('Question text cannot be empty');
      return;
    }
    
    if (!editedQuestion.option_a.trim() || !editedQuestion.option_b.trim() || 
        !editedQuestion.option_c.trim() || !editedQuestion.option_d.trim()) {
      alert('All answer options must be filled');
      return;
    }
    
    if (!['A', 'B', 'C', 'D'].includes(editedQuestion.correct_answer)) {
      alert('Please select a valid correct answer (A, B, C, or D)');
      return;
    }
    
    // Convert back to nested options structure
    const updatedQuestion = {
      question: editedQuestion.question,
      options: {
        A: editedQuestion.option_a,
        B: editedQuestion.option_b,
        C: editedQuestion.option_c,
        D: editedQuestion.option_d
      },
      correct_answer: editedQuestion.correct_answer,
      explanation: editedQuestion.explanation
    };
    
    // Update the question in the generatedQuestions array
    setGeneratedQuestions(prev => {
      const newQuestions = [...prev];
      newQuestions[index] = updatedQuestion;
      return newQuestions;
    });
    
    // Clear editing state
    cancelEditingQuestion(index);
    
    alert('Question updated successfully!');
  };

  const handleQuestionInputChange = (index, field, value) => {
    setEditingQuestionData(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  const deleteQuestion = (index) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setGeneratedQuestions(prev => prev.filter((_, i) => i !== index));
      // Clear any editing state for this question
      cancelEditingQuestion(index);
      alert('Question deleted successfully!');
    }
  };

  const addNewQuestion = () => {
    const newQuestion = {
      question: 'New question - click edit to modify',
      options: {
        A: 'Option A',
        B: 'Option B', 
        C: 'Option C',
        D: 'Option D'
      },
      correct_answer: 'A',
      explanation: 'Explanation for the correct answer',
      question_order: generatedQuestions.length + 1,
      batch_number: 1
    };
    
    setGeneratedQuestions(prev => [...prev, newQuestion]);
    alert('New question added! Click edit to customize it.');
  };

  // Scroll to top on route change
  useEffect(() => {
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
  }, [location.pathname]);
  // Link checker state
  const [linkCheckLoading, setLinkCheckLoading] = useState(false);
  const [linkCheckResult, setLinkCheckResult] = useState(null);
  const [showBadOnly, setShowBadOnly] = useState(false);

  // YouTube validator state
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [ytLoading, setYtLoading] = useState(false);
  const [ytError, setYtError] = useState('');
  const [ytEmbeddable, setYtEmbeddable] = useState(null); // null | true | false
  const [ytVideoId, setYtVideoId] = useState('');
  const [ytTitle, setYtTitle] = useState('');
  const [ytQuestions, setYtQuestions] = useState([]);
  const [ytQuestionsDirty, setYtQuestionsDirty] = useState(false);
  const [ytPlayerErrorCode, setYtPlayerErrorCode] = useState(null);
  const ytPlayerContainerRef = useRef(null);
  const ytPlayerInstanceRef = useRef(null);
  // YouTube publish metadata
  const [ytCategory, setYtCategory] = useState('');
  const [ytSecondaryCategory, setYtSecondaryCategory] = useState('');
  const [ytLanguage, setYtLanguage] = useState('English');
  const [ytPePoints, setYtPePoints] = useState(0.25);
  const [ytDifficulty, setYtDifficulty] = useState('Intermediate');
  const [ytPublishing, setYtPublishing] = useState(false);
  const [ytSummary, setYtSummary] = useState('');
  const [ytWatchTime, setYtWatchTime] = useState(10); // minutes
  // Podcasts state
  const [podUrl, setPodUrl] = useState('');
  const [podPlatform, setPodPlatform] = useState('');
  const [podEmbedUrl, setPodEmbedUrl] = useState('');
  const [podTitle, setPodTitle] = useState('');
  const [podSummary, setPodSummary] = useState('');
  const [podCategory, setPodCategory] = useState('');
  const [podSecondaryCategory, setPodSecondaryCategory] = useState('');
  const [podLanguage, setPodLanguage] = useState('English');
  const [podPePoints, setPodPePoints] = useState(0.25);
  const [podDifficulty, setPodDifficulty] = useState('Intermediate');
  const [podQuestions, setPodQuestions] = useState([]);
  const [podLoading, setPodLoading] = useState(false);
  const [podError, setPodError] = useState('');
  const [podPublishing, setPodPublishing] = useState(false);
  const [podWatchTime, setPodWatchTime] = useState(10); // minutes
  const runLinkCheck = async () => {
    try {
      setLinkCheckLoading(true);
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/admin/check-external-links`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setLinkCheckResult(data);
      } else {
        alert(data.error || 'Link check failed');
      }
    } catch (e) {
      console.error('Link check error:', e);
      alert('Network error');
    } finally {
      setLinkCheckLoading(false);
    }
  };

  // Helpers for YouTube validation without API key (uses oEmbed)
  const extractYouTubeId = (url) => {
    try {
      const u = new URL(url);
      if (u.hostname === 'youtu.be') {
        return u.pathname.replace('/', '');
      }
      if (u.searchParams.get('v')) {
        return u.searchParams.get('v');
      }
      // Embed URLs
      const parts = u.pathname.split('/');
      const embedIdx = parts.findIndex((p) => p === 'embed');
      if (embedIdx !== -1 && parts[embedIdx + 1]) return parts[embedIdx + 1];
      return '';
    } catch (e) {
      return '';
    }
  };

  const validateYouTube = async () => {
    setYtError('');
    setYtEmbeddable(null);
    const id = extractYouTubeId(youtubeUrl.trim());
    if (!id) {
      setYtError('Please enter a valid YouTube URL');
      return;
    }
    setYtVideoId(id);
    setYtLoading(true);
    try {
      // Prefer authoritative backend preflight (keeps API key on server)
      const preflightRes = await fetch(`${API_BASE_URL}/admin/youtube-preflight?videoId=${encodeURIComponent(id)}&country=NL`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      let pre;
      try {
        pre = await preflightRes.json();
      } catch {
        pre = { ok: false, reason: 'server_error' };
      }
      if (!pre.ok) {
        const map = {
          not_embeddable: 'Owner blocks embedding for this video.',
          age_restricted: 'Age-restricted videos cannot be embedded here.',
          region_blocked: 'This video is blocked in NL.',
          region_not_allowed: 'This video is not available in NL.',
          not_found: 'Video not found or unavailable.',
          server_missing_api_key: 'Server missing API key — falling back to oEmbed.',
          data_api_error: 'Data API error — falling back to oEmbed.',
          server_error: 'Server error — falling back to oEmbed.'
        };
        // Only hard-fail for definitive reasons; otherwise continue to oEmbed fallback
        if (pre.reason && ['not_embeddable','age_restricted','region_blocked','region_not_allowed','not_found'].includes(pre.reason)) {
          setYtEmbeddable(false);
          setYtError((map[pre.reason] || 'Not embeddable.') + (pre.status ? ` (status ${pre.status})` : '') + (pre.message ? ` — ${pre.message}` : ''));
          return;
        } else {
          setYtError((map[pre.reason] || 'Proceeding with fallback…') + (pre.status ? ` (status ${pre.status})` : '') + (pre.message ? ` — ${pre.message}` : ''));
        }
      }
      // oEmbed returns 200 if embeddable
      const oembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${id}&format=json`;
      const res = await fetch(oembedUrl, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        // Provisional yes, subject to runtime domain restriction test
        setYtEmbeddable(true);
        setYtTitle(pre?.title || data?.title || '');

        // Runtime domain restriction test (101/150). Some videos report embeddable=true
        // but are allow‑listed to specific domains. We create a hidden player and trap onError.
        try {
          const ensureYTReady = () => new Promise((resolve) => {
            if (window.YT && window.YT.Player) return resolve();
            const id = 'youtube-iframe-api';
            if (!document.getElementById(id)) {
              const tag = document.createElement('script');
              tag.src = 'https://www.youtube.com/iframe_api';
              tag.id = id;
              document.body.appendChild(tag);
            }
            const prev = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
              if (typeof prev === 'function') prev();
              resolve();
            };
          });

          await ensureYTReady();
          const container = document.createElement('div');
          container.style.position = 'absolute';
          container.style.left = '-9999px';
          container.style.width = '1px';
          container.style.height = '1px';
          document.body.appendChild(container);

          const result = await new Promise((resolve) => {
            let settled = false;
            let played = false;
            let guardTimer = null;
            const cleanup = () => {
              setTimeout(() => {
                try { container && container.parentNode && container.parentNode.removeChild(container); } catch {}
              }, 800);
            };
            /* eslint-disable no-new */
            new window.YT.Player(container, {
              videoId: id,
              playerVars: { origin: window.location.origin, enablejsapi: 1 },
              events: {
                onReady: (evt) => {
                  try { evt.target.mute(); evt.target.playVideo(); } catch {}
                  guardTimer = setTimeout(() => {
                    if (!settled && !played) { settled = true; console.debug('[YT-embed-check] no_play', { id }); cleanup(); resolve({ ok: false, code: 'no_play' }); }
                  }, 2500);
                },
                onStateChange: (s) => {
                  if (s && s.data === 1 && !settled) { // PLAYING
                    played = true; clearTimeout(guardTimer); settled = true; console.debug('[YT-embed-check] playing ok', { id }); cleanup(); resolve({ ok: true });
                  }
                },
                onError: (ev) => {
                  if (!settled) { settled = true; const code = ev && typeof ev.data !== 'undefined' ? ev.data : 'unknown'; console.debug('[YT-embed-check] onError', { id, code }); cleanup(); resolve({ ok: false, code }); }
                }
              }
            });
          });
          // container removal handled in cleanup()
          if (!result.ok && (result.code === 101 || result.code === 150)) {
            setYtEmbeddable(false);
            setYtError('Owner blocks embedding for this domain (101/150).');
            return;
          }
          if (!result.ok && result.code === 'no_play') {
            setYtEmbeddable(false);
            setYtError('Video cannot start playback in embed (age-restricted or “watch on YouTube only”).');
            return;
          }
        } catch (e) {
          // Non‑fatal; keep oEmbed result
        }
        // sensible defaults for meta
        if (!ytCategory) setYtCategory('Accounting');
        // Load draft if exists
        const draftKey = `yt_draft_${id}`;
        const draft = localStorage.getItem(draftKey);
        if (draft) {
          try {
            const parsed = JSON.parse(draft);
            if (Array.isArray(parsed?.questions)) setYtQuestions(parsed.questions);
            if (parsed?.title) setYtTitle(parsed.title);
            if (parsed?.category) setYtCategory(parsed.category);
            if (parsed?.secondary_category) setYtSecondaryCategory(parsed.secondary_category);
            if (parsed?.language) setYtLanguage(parsed.language);
            if (parsed?.pe_points_value) setYtPePoints(parsed.pe_points_value);
            if (parsed?.difficulty_level) setYtDifficulty(parsed.difficulty_level);
            if (parsed?.summary) setYtSummary(parsed.summary);
          } catch {}
        } else {
          setYtQuestions([]);
        }
      } else {
        setYtEmbeddable(false);
        setYtError('This video cannot be embedded.');
      }
    } catch (e) {
      setYtEmbeddable(false);
      setYtError('Network error while checking embeddability.');
    } finally {
      setYtLoading(false);
    }
  };

  // Update existing YouTube article using the YouTube fields
  const updateYouTubeArticle = async () => {
    if (!editingArticle) return;
    try {
      const token = localStorage.getItem('admin_token');
      // Build a PATCH-like payload: include only fields that actually changed
      const body = {};
      const numWatch = parseInt(ytWatchTime, 10);
      const fields = [
        ['title', ytTitle, editingArticle.title],
        ['summary', ytSummary, editingArticle.summary],
        ['category', ytCategory, editingArticle.category],
        ['secondary_category', ytSecondaryCategory, editingArticle.secondary_category || ''],
        ['language', ytLanguage, editingArticle.language],
        ['pe_points_value', Number(ytPePoints), editingArticle.pe_points_value],
        ['difficulty_level', ytDifficulty, editingArticle.difficulty_level]
      ];
      fields.forEach(([key, val, prev]) => {
        if (val !== undefined && val !== null && String(val) !== String(prev)) {
          body[key] = val;
        }
      });
      if (!Number.isNaN(numWatch) && numWatch > 0 && numWatch !== (editingArticle.estimated_read_time || 0)) {
        body.estimated_read_time = numWatch;
      }
      // Always ensure external_url and author/source are correct for YT
      body.external_url = editingArticle.external_url;
      body.author = 'YouTube';
      body.source = 'YouTube';
      body.is_published = true;

      const articleResponse = await fetch(`${API_BASE_URL}/articles/${editingArticle.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!articleResponse.ok) {
        const err = await articleResponse.json().catch(()=>({ error:'Update failed'}));
        alert(err.error || 'Failed to update YouTube article');
        return;
      }

      // Update questions only if user modified them and all prompts are present
      if (ytQuestionsDirty && (ytQuestions||[]).length > 0 && ytQuestions.every(q => String(q.prompt||'').trim().length > 0)) {
        const qRes = await fetch(`${API_BASE_URL}/articles/${editingArticle.id}/questions`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            questions: ytQuestions.map((q, i) => ({
              question: q.prompt || '',
              option_a: q.option_a || '',
              option_b: q.option_b || '',
              option_c: q.option_c || '',
              option_d: q.option_d || '',
              correct_answer: q.correct || 'A',
              explanation: q.explanation || '',
              question_order: i + 1,
              batch_number: 1
            }))
          })
        });
        if (!qRes.ok) {
          const qErr = await qRes.json().catch(()=>({ error:'Question update failed'}));
          alert(`Article updated but questions failed: ${qErr.error}`);
        }
        // reset dirty flag after successful PUT
        try { setYtQuestionsDirty(false); } catch {}

        // Immediately re-fetch questions from backend to verify and reflect saved state
        try {
          const verify = await fetch(`${API_BASE_URL}/articles/${editingArticle.id}/tests`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (verify.ok) {
            const saved = await verify.json();
            const mapped = (saved || []).map((q) => ({
              prompt: q.question || '',
              option_a: q.option_a || q.options?.A || '',
              option_b: q.option_b || q.options?.B || '',
              option_c: q.option_c || q.options?.C || '',
              option_d: q.option_d || q.options?.D || '',
              correct: q.correct_answer || 'A'
            }));
            setYtQuestions(mapped);
            if ((mapped || []).length === 0) {
              alert('Let op: geen vragen opgeslagen. Vul alle velden (prompt + 4 opties + correct) in en probeer opnieuw.');
            }
          }
        } catch {}
      } else if ((ytQuestions||[]).length > 0) {
        // Warn and preserve existing questions if prompts are missing
        console.warn('Skipping questions update: one or more prompts are empty.');
      }

      alert('YouTube video updated successfully!');
      fetchArticles();
      navigate(-1);
    } catch (e) {
      console.error('Update YouTube article error:', e);
      alert('Unexpected error while updating');
    }
  };

  // Podcast detection + embed builder (Spotify, Apple Podcasts, SoundCloud, Anchor)
  const detectPodcastEmbed = (url) => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace('www.', '').toLowerCase();
      if (host === 'open.spotify.com') {
        // /episode/:id or /show/:id
        const path = u.pathname.replace(/^\//, '');
        return { platform: 'Spotify', embedUrl: `https://open.spotify.com/embed/${path}` };
      }
      if (host === 'podcasts.apple.com' || host === 'itunes.apple.com') {
        return { platform: 'Apple Podcasts', embedUrl: url.replace('podcasts.apple.com', 'embed.podcasts.apple.com') };
      }
      if (host === 'soundcloud.com' || host === 'on.soundcloud.com') {
        return {
          platform: 'SoundCloud',
          embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23000000&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`
        };
      }
      if (host === 'anchor.fm') {
        return { platform: 'Anchor', embedUrl: url.includes('/episodes/') ? `${url}/embed` : url };
      }
      return { platform: '', embedUrl: '' };
    } catch {
      return { platform: '', embedUrl: '' };
    }
  };

  const validatePodcast = async () => {
    setPodError('');
    setPodLoading(true);
    try {
      const { platform, embedUrl } = detectPodcastEmbed(podUrl.trim());
      if (!platform || !embedUrl) {
        setPodError('Unsupported or invalid podcast URL');
        setPodPlatform('');
        setPodEmbedUrl('');
        return;
      }
      setPodPlatform(platform);
      setPodEmbedUrl(embedUrl);
      if (!podTitle) setPodTitle('');
    } finally {
      setPodLoading(false);
    }
  };

  // Create the YouTube IFrame Player API player and trap runtime errors
  useEffect(() => {
    if (!ytEmbeddable || !ytVideoId || !ytPlayerContainerRef.current) {
      return;
    }
    setYtPlayerErrorCode(null);

    const createPlayer = () => {
      if (!window.YT || !window.YT.Player) return false;
      // Destroy previous instance if any
      try {
        if (ytPlayerInstanceRef.current && ytPlayerInstanceRef.current.destroy) {
          ytPlayerInstanceRef.current.destroy();
        }
      } catch {}
      ytPlayerInstanceRef.current = new window.YT.Player(ytPlayerContainerRef.current, {
        videoId: ytVideoId,
        playerVars: { rel: 0, modestbranding: 1 },
        events: {
          onReady: () => setYtPlayerErrorCode(null),
          onError: (event) => {
            const code = event && typeof event.data !== 'undefined' ? event.data : 'unknown';
            setYtPlayerErrorCode(code);
          }
        }
      });
      return true;
    };

    // Load the IFrame API if not already
    if (!window.YT || !window.YT.Player) {
      const existingScript = document.getElementById('youtube-iframe-api');
      if (!existingScript) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.id = 'youtube-iframe-api';
        document.body.appendChild(tag);
      }
      const prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prevReady === 'function') prevReady();
        createPlayer();
      };
    } else {
      createPlayer();
    }

    return () => {
      try {
        if (ytPlayerInstanceRef.current && ytPlayerInstanceRef.current.destroy) {
          ytPlayerInstanceRef.current.destroy();
        }
      } catch {}
    };
  }, [ytEmbeddable, ytVideoId]);

  const addYtQuestion = () => {
    setYtQuestions(prev => [...prev, { prompt: '', option_a: '', option_b: '', option_c: '', option_d: '', correct: 'A', explanation: '' }]);
    setYtQuestionsDirty(true);
  };

  const updateYtQuestion = (idx, field, value) => {
    setYtQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
    setYtQuestionsDirty(true);
  };

  const removeYtQuestion = (idx) => {
    setYtQuestions(prev => prev.filter((_, i) => i !== idx));
    setYtQuestionsDirty(true);
  };

  const persistYtDraft = () => {
    if (!ytVideoId) return;
    const draftKey = `yt_draft_${ytVideoId}`;
    const payload = {
      title: ytTitle,
      videoId: ytVideoId,
      questions: ytQuestions,
      category: ytCategory,
      secondary_category: ytSecondaryCategory,
      language: ytLanguage,
      pe_points_value: ytPePoints,
      difficulty_level: ytDifficulty,
      summary: ytSummary
    };
    localStorage.setItem(draftKey, JSON.stringify(payload));
  };

  const publishYouTubeAsArticle = async () => {
    try {
      if (!ytVideoId || !ytTitle || !ytCategory || !ytLanguage) {
        alert('Please provide title, category, and language.');
        return;
      }
      setYtPublishing(true);
      const token = localStorage.getItem('admin_token');
      const body = {
        title: ytTitle,
        author: 'YouTube',
        summary: ytSummary,
        category: ytCategory,
        secondary_category: ytSecondaryCategory || undefined,
        language: ytLanguage,
        pe_points_value: ytPePoints,
        difficulty_level: ytDifficulty,
        estimated_read_time: Number(ytWatchTime) || 10,
        is_published: true,
        external_url: `https://www.youtube.com/watch?v=${ytVideoId}`,
        questions: ytQuestions.map((q, i) => ({
          question: q.prompt || '',
          option_a: q.option_a || '',
          option_b: q.option_b || '',
          option_c: q.option_c || '',
          option_d: q.option_d || '',
          correct_answer: q.correct || 'A',
          explanation: q.explanation || '',
          question_order: i + 1,
          batch_number: 1
        }))
      };
      const res = await fetch(`${API_BASE_URL}/create-article-with-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data?.error || 'Failed to publish YouTube article');
        return;
      }
      alert('YouTube video published as article successfully!');
      try { localStorage.removeItem(`yt_draft_${ytVideoId}`); } catch {}
    } catch (e) {
      console.error('Publish YouTube error:', e);
      alert('Unexpected error while publishing.');
    } finally {
      setYtPublishing(false);
    }
  };

  const publishPodcastAsArticle = async () => {
    try {
      setPodPublishing(true);
      const token = localStorage.getItem('admin_token');
      const body = {
        title: podTitle || 'Podcast',
        summary: podSummary || '',
        author: podPlatform || 'Podcast',
        source: podPlatform || 'Podcast',
        category: podCategory || '',
        secondary_category: podSecondaryCategory || '',
        language: podLanguage || 'English',
        pe_points_value: Number(podPePoints) || 0.25,
        difficulty_level: podDifficulty || 'Intermediate',
        estimated_read_time: Number(podWatchTime) || 10,
        external_url: podUrl.trim(),
        questions: podQuestions && podQuestions.length ? podQuestions : []
      };
      const res = await fetch(`${API_BASE_URL}/create-article-with-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data?.error || 'Failed to publish Podcast content');
        return;
      }
      alert('Podcast published successfully!');
      setPodUrl(''); setPodPlatform(''); setPodEmbedUrl(''); setPodTitle(''); setPodSummary(''); setPodQuestions([]);
    } catch (e) {
      console.error('Publish podcast error:', e);
      alert('Unexpected error while publishing');
    } finally {
      setPodPublishing(false);
    }
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-brand" onClick={() => {
          navigate('/dashboard');
          setEditingArticle(null);
        }} style={{ cursor: 'pointer' }}>
          <h2>Protagon - Admin Dashboard</h2>
        </div>
        <div className="nav-content">
          <div className="nav-info">
            <span>Welcome, {user.first_name} {user.last_name}</span>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => {
              // Transfer admin credentials to main user dashboard
              const adminToken = localStorage.getItem('admin_token');
              
              // Open main user dashboard in new tab with admin credentials - redirect to main dashboard instead of login
              const userDashboardUrl = `https://cpd-pro.app/?admin_token=${adminToken}&user_data=${encodeURIComponent(JSON.stringify(user))}`;
              window.open(userDashboardUrl, '_blank');
            }}
            style={{
              marginRight: '1rem',
              background: '#4CAF50',
              borderColor: '#4CAF50'
            }}
          >
            🚀 Access User Dashboard
          </button>
          <button className="btn btn-secondary logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </nav>

      <main className="main-content">
        <div className="admin-header">
          <div className="admin-nav">
            <button 
              className={`nav-tab ${location.pathname === '/dashboard' || location.pathname === '/' || location.pathname.startsWith('/add-article') || location.pathname.startsWith('/edit-article') ? 'active' : ''}`}
              onClick={() => navigate('/dashboard')}
            >
              📚 Articles Management
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/news-management' ? 'active' : ''}`}
              onClick={() => navigate('/news-management')}
            >
              📰 News Management
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/feedback-management' ? 'active' : ''}`}
              onClick={() => navigate('/feedback-management')}
            >
              📝 Feedback Management
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/discount-codes' ? 'active' : ''}`}
              onClick={() => navigate('/discount-codes')}
            >
              🎟️ Discount Codes
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/article-requests' ? 'active' : ''}`}
              onClick={() => navigate('/article-requests')}
            >
              📝 Article Requests
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/external-links' ? 'active' : ''}`}
              onClick={()=>navigate('/external-links')}
            >
              🔗 External Links
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/youtube-videos' ? 'active' : ''}`}
              onClick={()=>navigate('/youtube-videos')}
            >
              📺 YouTube Videos
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/podcasts' ? 'active' : ''}`}
              onClick={()=>navigate('/podcasts')}
            >
              🎧 Podcasts
            </button>
          </div>
          
          {(location.pathname === '/dashboard' || location.pathname === '/' || location.pathname.startsWith('/add-article') || location.pathname.startsWith('/edit-article')) && (
            <button 
              className="btn btn-primary"
              onClick={() => {
                if (location.pathname === '/add-article') {
                  navigate(-1); // Go back to previous page
                } else {
                  navigate('/add-article');
                }
              }}
            >
              {location.pathname === '/add-article' ? 'Cancel' : 'Add New Article'}
            </button>
          )}
        </div>

        {/* Add Article Form */}
        {location.pathname === '/add-article' && (
          <div className="add-article-form">
            <h2>Add New Article</h2>
            
            {/* PDF Upload Section */}
            <div className="pdf-upload-section" style={{
              background: '#f8f9fa',
              border: '2px dashed #dee2e6',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <h3 style={{ marginBottom: '15px', color: '#495057' }}>
                🚀 AI-Powered Content Processing
              </h3>
              
              {/* Mode Selection */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="manual"
                      checked={processingMode === 'manual'}
                      onChange={(e) => setProcessingMode(e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    ✏️ Manual Text Input (Recommended)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="pdf"
                      checked={processingMode === 'pdf'}
                      onChange={(e) => setProcessingMode(e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    📄 PDF Upload
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="external"
                      checked={processingMode === 'external'}
                      onChange={(e) => setProcessingMode(e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    🔗 External Link
                  </label>
                </div>
              </div>

              {/* Manual Text Processing */}
              {processingMode === 'manual' && (
                <div>
                  <p style={{ marginBottom: '15px', color: '#000000', fontWeight: 'bold', background: '#ffeb3b', padding: '10px', borderRadius: '4px' }}>
                    📝 Step 1: Paste your article content in the "Content" field below, then click "Generate Questions with AI"
                  </p>
                  <button
                    type="button"
                    onClick={handleProcessManualText}
                    disabled={!newArticle.content.trim() || manualTextProcessing}
                    className="btn btn-success"
                    style={{
                      opacity: (!newArticle.content.trim() || manualTextProcessing) ? 0.6 : 1,
                      cursor: (!newArticle.content.trim() || manualTextProcessing) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      padding: '12px 24px',
                      backgroundColor: '#ff5722',
                      border: 'none',
                      color: 'white',
                      borderRadius: '6px'
                    }}
                  >
                    {manualTextProcessing ? '🤖 Processing with AI...' : '🤖 Generate Questions with AI'}
                  </button>
                  {!newArticle.content.trim() && (
                    <p style={{ marginTop: '10px', color: '#ffffff', background: '#f44336', padding: '8px', borderRadius: '4px', fontStyle: 'italic' }}>
                      ⬇️ Please scroll down and fill in the "Content" field first
                    </p>
                  )}
                </div>
              )}

              {/* PDF Upload */}
              {processingMode === 'pdf' && (
                <div>
                  <p style={{ marginBottom: '15px', color: '#6c757d' }}>
                    🚀 <strong>Enhanced PDF Processing:</strong> Upload a PDF file to automatically extract text content using advanced OCR technology (supports both text-based and scanned PDFs). The system will generate high-quality questions and summaries using GPT-4.
                  </p>
                  <div style={{ 
                    background: '#e3f2fd', 
                    border: '1px solid #2196f3', 
                    borderRadius: '4px', 
                    padding: '10px', 
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}>
                    <strong>✨ New Features:</strong>
                    <br />• OCR support for scanned/image-based PDFs
                    <br />• Enhanced GPT-4 question generation
                    <br />• Improved content summaries
                    <br />• Better question difficulty balancing
                  </div>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileSelect}
                      style={{
                        padding: '8px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        marginRight: '10px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handlePdfUpload}
                      disabled={!selectedFile || uploadLoading}
                      className="btn btn-primary"
                      style={{
                        opacity: (!selectedFile || uploadLoading) ? 0.6 : 1,
                        cursor: (!selectedFile || uploadLoading) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {uploadLoading ? '🔄 Processing with OCR & AI...' : '📄 Process PDF with OCR & AI'}
                    </button>
                  </div>
                  
                  {selectedFile && (
                    <p style={{ color: '#28a745', marginBottom: '10px' }}>
                      ✓ Selected: {selectedFile.name}
                    </p>
                  )}
                </div>
              )}
              
              {processingMode === 'external' && (
                <div>
                  <p style={{ marginBottom: '15px', color: '#6c757d' }}>
                    🔗 <strong>External Link with Manual Content:</strong> Enter the URL for reference, then manually paste the article content into the "Content" field below. The system will generate questions and summary using GPT-4 based on your pasted content.
                  </p>
                  <div style={{ 
                    background: '#fff3e0', 
                    border: '1px solid #ff9800', 
                    borderRadius: '4px', 
                    padding: '10px', 
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}>
                    <strong>📝 How it works:</strong>
                    <br />• Enter the article URL below (for reference)
                    <br />• Manually copy and paste the article content into the "Content" field
                    <br />• Click "Generate Questions & Summary" to process with GPT-4
                    <br />• AI will create professional questions and summary based on your content
                  </div>
                  <div style={{ 
                    background: '#e8f5e8', 
                    border: '1px solid #28a745', 
                    borderRadius: '4px', 
                    padding: '12px', 
                    marginBottom: '15px',
                    fontSize: '14px',
                    color: '#155724'
                  }}>
                    <strong>💡 UNLIMITED Content Support:</strong>
                    <br />• This field has <strong>NO SIZE LIMITS</strong> - paste entire books if needed!
                    <br />• Supports 100,000+ words easily
                    <br />• Auto-expands to any height - no scrolling needed
                    <br />• Perfect for comprehensive academic papers, technical documents, and research
                    <br />• <strong>Just paste everything and let the system handle it!</strong>
                  </div>
                  <input
                    type="url"
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    placeholder="https://example.com/path/to/article (for reference)"
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ced4da', marginBottom: '15px' }}
                  />
                  <button
                    type="button"
                    onClick={handleProcessExternalLink}
                    disabled={!externalUrl.trim() || !newArticle.content.trim() || manualTextProcessing}
                    className="btn btn-success"
                    style={{
                      opacity: (!externalUrl.trim() || !newArticle.content.trim() || manualTextProcessing) ? 0.6 : 1,
                      cursor: (!externalUrl.trim() || !newArticle.content.trim() || manualTextProcessing) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      padding: '12px 24px',
                      backgroundColor: '#4CAF50',
                      border: 'none',
                      color: 'white',
                      borderRadius: '6px'
                    }}
                  >
                    {manualTextProcessing ? '🤖 Generating Questions & Summary...' : '🤖 Generate Questions & Summary'}
                  </button>
                  {(!externalUrl.trim() || !newArticle.content.trim()) && (
                    <div style={{ marginTop: '10px' }}>
                      {!externalUrl.trim() && (
                        <p style={{ color: '#ffffff', background: '#f44336', padding: '8px', borderRadius: '4px', fontStyle: 'italic', marginBottom: '5px' }}>
                          ⬆️ Please enter the article URL above
                        </p>
                      )}
                  {!newArticle.content.trim() && (
                        <p style={{ color: '#ffffff', background: '#f44336', padding: '8px', borderRadius: '4px', fontStyle: 'italic' }}>
                          ⬇️ Please scroll down and paste the article content in the "Content" field
                    </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {uploadSuccess && (
                <p style={{ color: '#28a745', fontWeight: 'bold' }}>
                  ✓ Content processed successfully! Form fields have been auto-filled and questions generated.
                </p>
              )}
            </div>

            <form onSubmit={handleCreateArticleWithQuestions}>
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={newArticle.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Author *</label>
                  <input
                    type="text"
                    name="author"
                    value={newArticle.author}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Source</label>
                  <input
                    type="text"
                    name="source"
                    value={newArticle.source}
                    onChange={handleInputChange}
                    placeholder="e.g., IEEE Journal, Engineering.com, Textbook..."
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={newArticle.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {PROFESSION_CATEGORIES.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Secondary Category (optional)</label>
                  <select
                    name="secondary_category"
                    value={newArticle.secondary_category || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">None</option>
                    {PROFESSION_CATEGORIES.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Subcategory (optional)</label>
                  <input
                    type="text"
                    name="subcategory"
                    value={newArticle.subcategory}
                    onChange={handleInputChange}
                    placeholder="e.g., M&A"
                  />
                </div>
                <div className="form-group">
                  <label>Language *</label>
                  <select
                    name="language"
                    value={newArticle.language}
                    onChange={handleInputChange}
                    required
                  >
                    {LANGUAGES.map(language => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Difficulty Level</label>
                  <select
                    name="difficulty_level"
                    value={newArticle.difficulty_level}
                    onChange={handleInputChange}
                  >
                    {DIFFICULTY_LEVELS.map(level => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>PE Points Value</label>
                  <select
                    name="pe_points_value"
                    value={newArticle.pe_points_value}
                    onChange={handleInputChange}
                  >
                    {PE_POINTS_VALUES.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estimated Read Time (minutes):</label>
                  <input
                    type="number"
                    name="estimated_read_time"
                    value={newArticle.estimated_read_time}
                    onChange={handleInputChange}
                    min="1"
                    max="800"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Publication Date:</label>
                  <input
                    type="date"
                    name="publication_date"
                    value={newArticle.publication_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Summary</label>
                <textarea
                  name="summary"
                  value={newArticle.summary}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Brief summary of the article..."
                />
              </div>

              <div className="form-group content-field">
                <label>Content *</label>
                {processingMode === 'manual' && (
                  <div style={{
                    background: '#2196f3',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    border: '3px solid #1976d2'
                  }}>
                    <strong>👆 PASTE YOUR ARTICLE TEXT HERE 👆</strong>
                    <br />
                    <small>After pasting your content, scroll up and click "Generate Questions with AI"</small>
                  </div>
                )}
                {processingMode === 'external' && (
                  <div style={{
                    background: '#4CAF50',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    border: '3px solid #388E3C'
                  }}>
                    <strong>📝 PASTE THE ARTICLE CONTENT FROM YOUR EXTERNAL LINK HERE 📝</strong>
                    <br />
                    <small>After pasting content and entering the URL above, click "Generate Questions & Summary"</small>
                  </div>
                )}
                <textarea
                  name="content"
                  value={newArticle.content}
                  onChange={handleInputChange}
                  rows="120"
                  required
                  className="content-textarea"
                  placeholder={
                    processingMode === 'manual' ? 
                    "🚀 UNLIMITED CAPACITY - Paste your complete article content here...\n\n✅ No size limits! Paste entire documents, books, research papers - anything!\n✅ Field auto-expands to fit your content\n✅ Perfect for massive content processing" : 
                    processingMode === 'external' ?
                      "🚀 UNLIMITED CAPACITY - Paste the article content from your external source here...\n\n✅ No size limits! This field handles enormous content effortlessly\n✅ Paste 100,000+ words if needed\n✅ Built for processing comprehensive documents" :
                    "Full article content (unlimited size)..."
                  }
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={newArticle.is_published}
                    onChange={handleInputChange}
                  />
                  Publish immediately
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={Object.keys(editingQuestions).length > 0}>
                  {Object.keys(editingQuestions).length > 0 
                    ? '⚠️ Please save or cancel question edits first' 
                    : generatedQuestions.length > 0 
                      ? 'Create Article with Generated Questions' 
                      : 'Add Article'
                  }
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={handleCancelForm}
                >
                  Cancel
                </button>
              </div>
            </form>

            {/* Generated Questions Display */}
            {showQuestions && generatedQuestions.length > 0 && (
              <div className="generated-questions" style={{
                marginTop: '30px',
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0, color: '#495057' }}>
                    🤖 GPT-4 Generated Questions ({generatedQuestions.length})
                  </h3>
                  <button
                    type="button"
                    onClick={addNewQuestion}
                    className="btn btn-success"
                    style={{ fontSize: '14px', padding: '8px 16px' }}
                  >
                    ➕ Add New Question
                  </button>
                </div>
                
                <p style={{ marginBottom: '20px', color: '#6c757d' }}>
                  GPT-4 has automatically generated comprehensive questions based on the article content. 
                  You can edit, delete, or add new questions below. Changes will be saved when you create the article.
                </p>
                
                <div style={{ 
                  maxHeight: '800px', 
                  overflowY: 'auto',
                  border: '2px solid #007bff',
                  borderRadius: '8px',
                  background: 'white',
                  padding: '15px'
                }}>
                  <div style={{
                    marginBottom: '15px',
                    padding: '10px',
                    background: '#e3f2fd',
                    borderRadius: '6px',
                    textAlign: 'center',
                    border: '2px solid #2196f3'
                  }}>
                    <strong style={{ color: '#1976d2', fontSize: '16px' }}>
                      📋 Review and edit all {generatedQuestions.length} questions before creating the article
                    </strong>
                  </div>

                  {generatedQuestions.map((question, index) => (
                    <div key={index} style={{
                      background: '#ffffff',
                      padding: '15px',
                      marginBottom: '15px',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        padding: '8px 12px',
                        background: '#f8f9fa',
                        borderRadius: '4px'
                      }}>
                        <h4 style={{ 
                          margin: 0,
                          color: '#2c3e50',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}>
                          Question {index + 1}:
                        </h4>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {!editingQuestions[index] ? (
                            <>
                              <button
                                type="button"
                                onClick={() => startEditingQuestion(index)}
                                className="btn btn-secondary"
                                style={{ 
                                  fontSize: '12px', 
                                  padding: '4px 8px',
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteQuestion(index)}
                                className="btn btn-danger"
                                style={{ 
                                  fontSize: '12px', 
                                  padding: '4px 8px',
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                🗑️ Delete
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => saveEditedQuestion(index)}
                                className="btn btn-success"
                                style={{ 
                                  fontSize: '12px', 
                                  padding: '4px 8px',
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                💾 Save
                              </button>
                              <button
                                type="button"
                                onClick={() => cancelEditingQuestion(index)}
                                className="btn btn-secondary"
                                style={{ 
                                  fontSize: '12px', 
                                  padding: '4px 8px',
                                  background: '#6c757d',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                ❌ Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {editingQuestions[index] ? (
                        // Edit mode
                        <div>
                          <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                              Question Text:
                            </label>
                            <textarea
                              value={editingQuestionData[index]?.question || ''}
                              onChange={(e) => handleQuestionInputChange(index, 'question', e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: '80px',
                                padding: '8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                              placeholder="Enter the question text..."
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Option A:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.option_a || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'option_a', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Option A text..."
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Option B:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.option_b || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'option_b', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Option B text..."
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Option C:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.option_c || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'option_c', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Option C text..."
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Option D:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.option_d || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'option_d', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Option D text..."
                              />
                            </div>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '15px', marginBottom: '15px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Correct Answer:
                              </label>
                              <select
                                value={editingQuestionData[index]?.correct_answer || 'A'}
                                onChange={(e) => handleQuestionInputChange(index, 'correct_answer', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              >
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Explanation:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.explanation || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'explanation', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Explanation for the correct answer..."
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div>
                          <p style={{ 
                            marginBottom: '12px', 
                            fontSize: '15px',
                            lineHeight: '1.5',
                            color: '#2c3e50',
                            fontWeight: '500'
                          }}>
                            {question.question}
                          </p>
                          <div style={{ 
                            background: '#f8f9fa',
                            padding: '10px',
                            borderRadius: '4px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div style={{ marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                              <strong>A)</strong> {question.options?.A || question.option_a || '[Empty option A]'}
                            </div>
                            <div style={{ marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                              <strong>B)</strong> {question.options?.B || question.option_b || '[Empty option B]'}
                            </div>
                            <div style={{ marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                              <strong>C)</strong> {question.options?.C || question.option_c || '[Empty option C]'}
                            </div>
                            <div style={{ marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                              <strong>D)</strong> {question.options?.D || question.option_d || '[Empty option D]'}
                            </div>
                            <div style={{
                              marginTop: '10px',
                              fontSize: '14px', 
                              color: '#28a745',
                              fontWeight: 'bold',
                              background: '#d4edda',
                              padding: '5px 8px',
                              borderRadius: '3px'
                            }}>
                              <strong>Correct Answer:</strong> {question.correct_answer}) {question.options?.[question.correct_answer] || question[`option_${question.correct_answer?.toLowerCase()}`] || '[Missing correct option]'}
                            </div>
                            {question.explanation && (
                              <div style={{
                                marginTop: '10px',
                                fontSize: '13px',
                                color: '#666',
                                fontStyle: 'italic',
                                background: '#e7f3ff',
                                padding: '5px 8px',
                                borderRadius: '3px'
                              }}>
                                <strong>Explanation:</strong> {question.explanation}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: '#d4edda',
                    borderRadius: '6px',
                    textAlign: 'center',
                    border: '2px solid #28a745'
                  }}>
                    <strong style={{ color: '#155724', fontSize: '16px' }}>
                      ✅ All {generatedQuestions.length} questions ready! Review edits and create article.
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Article Form - for non-YouTube */}
        {location.pathname.startsWith('/edit-article/') && editingArticle && !(editingArticle.external_url||'').includes('youtube.com') && !(editingArticle.external_url||'').includes('youtu.be') && (
          <div className="add-article-form">
            <h2>Edit Article: {editingArticle.title}</h2>
            
            <form onSubmit={handleUpdateArticle}>
              <div className="form-group">
                <label>Title:</label>
                <input
                  type="text"
                  name="title"
                  value={newArticle.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Author:</label>
                <input
                  type="text"
                  name="author"
                  value={newArticle.author}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Source</label>
                  <input
                    type="text"
                    name="source"
                    value={newArticle.source}
                    onChange={handleInputChange}
                    placeholder="e.g., IEEE Journal, Engineering.com, Textbook..."
                  />
                </div>
                <div className="form-group">
                  <label>Category:</label>
                  <select
                    name="category"
                    value={newArticle.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {PROFESSION_CATEGORIES.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Secondary Category (optional):</label>
                  <select
                    name="secondary_category"
                    value={newArticle.secondary_category || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">None</option>
                    {PROFESSION_CATEGORIES.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Language:</label>
                  <select
                    name="language"
                    value={newArticle.language}
                    onChange={handleInputChange}
                    required
                  >
                    {LANGUAGES.map(language => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Difficulty Level:</label>
                  <select
                    name="difficulty_level"
                    value={newArticle.difficulty_level}
                    onChange={handleInputChange}
                  >
                    {DIFFICULTY_LEVELS.map(level => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>PE Points Value:</label>
                  <select
                    name="pe_points_value"
                    value={newArticle.pe_points_value}
                    onChange={handleInputChange}
                    required
                  >
                    {PE_POINTS_VALUES.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estimated Read Time (minutes):</label>
                  <input
                    type="number"
                    name="estimated_read_time"
                    value={newArticle.estimated_read_time}
                    onChange={handleInputChange}
                    min="1"
                    max="300"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Publication Date:</label>
                  <input
                    type="date"
                    name="publication_date"
                    value={newArticle.publication_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Summary:</label>
                <textarea
                  name="summary"
                  value={newArticle.summary}
                  onChange={handleInputChange}
                  rows="3"
                  required
                  placeholder="Brief summary of the article..."
                />
              </div>

              {/* Show PDF info if editing article with PDF */}
              {pdfFilePath && (
                <div className="pdf-info" style={{
                  background: '#e8f5e8',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #28a745',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ color: '#155724', marginBottom: '10px' }}>📄 Current PDF File</h4>
                  <p style={{ color: '#155724', marginBottom: '10px' }}>
                    <strong>File:</strong> {pdfFilePath}
                  </p>
                  <p style={{ color: '#6c757d', fontSize: '14px' }}>
                    To replace the PDF, upload a new file using the "Add Article" form and copy the content.
                  </p>
                </div>
              )}

              {/* Show external URL info if editing article with external URL */}
              {externalUrl && (
                <div className="form-group">
                  <label>External Article URL</label>
                  <input
                    type="url"
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    placeholder="https://example.com/path/to/article"
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                  />
                </div>
              )}

              <div className="form-group content-field">
                <label>Content:</label>
                <div style={{
                  background: '#fff3cd',
                  border: '1px solid #ffeeba',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '8px',
                  color: '#856404'
                }}>
                  <strong>📝 Content Editing</strong>
                  <br />
                  <small>You can modify the article content directly. For PDF articles, this will show the extracted text.</small>
                </div>
                <textarea
                  name="content"
                  value={newArticle.content}
                  onChange={handleInputChange}
                  rows="120"
                  required
                  className="content-textarea"
                  placeholder="Article content..."
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={newArticle.is_published}
                    onChange={handleInputChange}
                  />
                  Published
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={Object.keys(editingQuestions).length > 0}>
                  {Object.keys(editingQuestions).length > 0 
                    ? '⚠️ Please save or cancel question edits first' 
                    : 'Update Article'
                  }
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={handleCancelForm}
                >
                  Cancel
                </button>
              </div>
            </form>

            {/* Current Questions Display */}
            {generatedQuestions.length > 0 && (
              <div className="current-questions" style={{
                marginTop: '30px',
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0, color: '#495057' }}>
                    📝 Current Questions ({generatedQuestions.length})
                  </h3>
                  <button
                    type="button"
                    onClick={addNewQuestion}
                    className="btn btn-success"
                    style={{ fontSize: '14px', padding: '8px 16px' }}
                  >
                    ➕ Add New Question
                  </button>
                </div>
                
                <p style={{ marginBottom: '20px', color: '#6c757d' }}>
                  These are the current questions for this article. You can edit, delete, or add new questions below. 
                  Changes will be saved when you update the article.
                </p>
                
                <div style={{ 
                  maxHeight: '800px', 
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  background: 'white',
                  padding: '15px'
                }}>
                  {generatedQuestions.map((question, index) => (
                    <div key={index} style={{
                      background: '#ffffff',
                      padding: '15px',
                      marginBottom: '15px',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        padding: '8px 12px',
                        background: '#f8f9fa',
                        borderRadius: '4px'
                      }}>
                        <h4 style={{ 
                          margin: 0,
                          color: '#2c3e50',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}>
                          Question {index + 1}:
                        </h4>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {!editingQuestions[index] ? (
                            <>
                              <button
                                type="button"
                                onClick={() => startEditingQuestion(index)}
                                className="btn btn-secondary"
                                style={{ 
                                  fontSize: '12px', 
                                  padding: '4px 8px',
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteQuestion(index)}
                                className="btn btn-danger"
                                style={{ 
                                  fontSize: '12px', 
                                  padding: '4px 8px',
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                🗑️ Delete
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => saveEditedQuestion(index)}
                                className="btn btn-success"
                                style={{ 
                                  fontSize: '12px', 
                                  padding: '4px 8px',
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                💾 Save
                              </button>
                              <button
                                type="button"
                                onClick={() => cancelEditingQuestion(index)}
                                className="btn btn-secondary"
                                style={{ 
                                  fontSize: '12px', 
                                  padding: '4px 8px',
                                  background: '#6c757d',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                ❌ Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {editingQuestions[index] ? (
                        // Edit mode
                        <div>
                          <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                              Question Text:
                            </label>
                            <textarea
                              value={editingQuestionData[index]?.question || ''}
                              onChange={(e) => handleQuestionInputChange(index, 'question', e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: '80px',
                                padding: '8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                              placeholder="Enter the question text..."
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Option A:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.option_a || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'option_a', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Option A text..."
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Option B:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.option_b || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'option_b', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Option B text..."
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Option C:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.option_c || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'option_c', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Option C text..."
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Option D:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.option_d || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'option_d', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Option D text..."
                              />
                            </div>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '15px', marginBottom: '15px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Correct Answer:
                              </label>
                              <select
                                value={editingQuestionData[index]?.correct_answer || 'A'}
                                onChange={(e) => handleQuestionInputChange(index, 'correct_answer', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              >
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Explanation:
                              </label>
                              <textarea
                                value={editingQuestionData[index]?.explanation || ''}
                                onChange={(e) => handleQuestionInputChange(index, 'explanation', e.target.value)}
                                style={{
                                  width: '100%',
                                  minHeight: '60px',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Explanation for the correct answer..."
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div>
                          <p style={{ 
                            marginBottom: '12px', 
                            fontSize: '15px',
                            lineHeight: '1.5',
                            color: '#2c3e50',
                            fontWeight: '500'
                          }}>
                            {question.question}
                          </p>
                          <div style={{ 
                            background: '#f8f9fa',
                            padding: '10px',
                            borderRadius: '4px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div style={{ marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                              <strong>A)</strong> {question.options?.A || question.option_a || '[Empty option A]'}
                            </div>
                            <div style={{ marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                              <strong>B)</strong> {question.options?.B || question.option_b || '[Empty option B]'}
                            </div>
                            <div style={{ marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                              <strong>C)</strong> {question.options?.C || question.option_c || '[Empty option C]'}
                            </div>
                            <div style={{ marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                              <strong>D)</strong> {question.options?.D || question.option_d || '[Empty option D]'}
                            </div>
                            <div style={{
                              marginTop: '10px',
                              fontSize: '14px', 
                              color: '#28a745',
                              fontWeight: 'bold',
                              background: '#d4edda',
                              padding: '5px 8px',
                              borderRadius: '3px'
                            }}>
                              <strong>Correct Answer:</strong> {question.correct_answer}) {question.options?.[question.correct_answer] || question[`option_${question.correct_answer?.toLowerCase()}`] || '[Missing correct option]'}
                            </div>
                            {question.explanation && (
                              <div style={{
                                marginTop: '10px',
                                fontSize: '13px',
                                color: '#666',
                                fontStyle: 'italic',
                                background: '#e7f3ff',
                                padding: '5px 8px',
                                borderRadius: '3px'
                              }}>
                                <strong>Explanation:</strong> {question.explanation}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit YouTube Video - use the YouTube template with prefilled values */}
        {location.pathname.startsWith('/edit-article/') && editingArticle && (((editingArticle.external_url||'').includes('youtube.com') || (editingArticle.external_url||'').includes('youtu.be'))) && (
          <div className="admin-section card">
            <div className="section-header">
              <h2>Edit YouTube Video</h2>
            </div>
            <div style={{padding:'1rem'}}>
              <div style={{marginBottom:8, color:'#6b7280'}}>Video URL: <a href={editingArticle.external_url} target="_blank" rel="noreferrer">{editingArticle.external_url}</a></div>
              <label style={{fontWeight:600, display:'block', marginBottom:8}}>Title</label>
              <input type="text" value={ytTitle || editingArticle.title} onChange={(e)=>setYtTitle(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6, marginBottom:12}} />

              <div style={{marginBottom:12}}>
                <label style={{fontWeight:600, display:'block', marginBottom:6}}>Summary</label>
                <textarea rows={3} value={ytSummary} onChange={(e)=>setYtSummary(e.target.value)} placeholder="Short summary users will see on the card" style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
              </div>

              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Category</label>
                  <select value={ytCategory || editingArticle.category || ''} onChange={(e)=>setYtCategory(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    <option value="">Select Category</option>
                    {PROFESSION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Watch Time (minutes)</label>
                  <input type="number" min="1" step="1" value={ytWatchTime ?? ''} onChange={(e)=>setYtWatchTime(e.target.value)} placeholder="e.g., 10" style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Secondary Category (optional)</label>
                  <select value={ytSecondaryCategory || editingArticle.secondary_category || ''} onChange={(e)=>setYtSecondaryCategory(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    <option value="">None</option>
                    {PROFESSION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Language</label>
                  <select value={ytLanguage || editingArticle.language || 'English'} onChange={(e)=>setYtLanguage(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>PE Points</label>
                  <select value={ytPePoints || editingArticle.pe_points_value || 0.25} onChange={(e)=>setYtPePoints(parseFloat(e.target.value))} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    {PE_POINTS_VALUES.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Difficulty</label>
                  <select value={ytDifficulty || editingArticle.difficulty_level || 'Intermediate'} onChange={(e)=>setYtDifficulty(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    {DIFFICULTY_LEVELS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              {/* Manual Questions reuse */}
              <div style={{marginTop:16}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <h3 style={{margin:0}}>Questions</h3>
                  <button className="btn" onClick={addYtQuestion}>+ Add Question</button>
                </div>
                {ytQuestions.length === 0 && (
                  <div style={{marginTop:8, color:'#6b7280'}}>No questions yet. (Questions will not be changed unless you edit/add them.)</div>
                )}
                {ytQuestions.map((q, idx) => (
                  <div key={idx} style={{border:'1px solid #e5e7eb', borderRadius:8, padding:'12px', marginTop:10}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                      <strong>Question {idx+1}</strong>
                      <button className="btn" onClick={()=>removeYtQuestion(idx)}>Remove</button>
                    </div>
                    <label style={{display:'block', fontWeight:600, marginBottom:4}}>Prompt</label>
                    <textarea value={q.prompt} onChange={(e)=>updateYtQuestion(idx,'prompt',e.target.value)} rows={2} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:8}}>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>Option A</label>
                        <input value={q.option_a} onChange={(e)=>updateYtQuestion(idx,'option_a',e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                      </div>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>Option B</label>
                        <input value={q.option_b} onChange={(e)=>updateYtQuestion(idx,'option_b',e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                      </div>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>Option C</label>
                        <input value={q.option_c} onChange={(e)=>updateYtQuestion(idx,'option_c',e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                      </div>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>Option D</label>
                        <input value={q.option_d} onChange={(e)=>updateYtQuestion(idx,'option_d',e.target.value)} style={{width:'100%', padding:'0.6rem', border:"1px solid #d1d5db", borderRadius:6}} />
                      </div>
                    </div>
                    <div style={{marginTop:8}}>
                      <label style={{fontWeight:600, marginRight:8}}>Correct:</label>
                      {['A','B','C','D'].map(opt => (
                        <label key={opt} style={{marginRight:12}}>
                          <input type="radio" name={`yt_correct_${idx}`} checked={q.correct===opt} onChange={()=>updateYtQuestion(idx,'correct',opt)} /> {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{marginTop:16}}>
                <button className="btn btn-primary" onClick={updateYouTubeArticle}>Update</button>
                <button className="btn" style={{marginLeft:8}} onClick={persistYtDraft}>Save Draft</button>
              </div>
            </div>
          </div>
        )}

        {/* Articles List */}
        {location.pathname === '/dashboard' && (
        <div className="articles-section">
          <div className="articles-header">
            <h2>Article Management</h2>
            <div className="article-stats">
              <span>Total: {articles.length}</span>
              <span>Filtered: {filteredArticles.length}</span>
            </div>
          </div>

          {/* Filtering Controls */}
          <div className="filtering-section">
            {/* Archive Toggle */}
            <div style={{ marginBottom: '1rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.9)', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: '600', color: '#2d3748' }}>
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  style={{ transform: 'scale(1.2)' }}
                />
                📁 Show Archived Articles
              </label>
              <small style={{ marginLeft: '1.7rem', color: '#6c757d' }}>
                {showArchived ? 'Showing all articles including archived ones' : 'Hiding archived articles'}
              </small>
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>Filter by Category:</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="filter-select"
                >
                  <option value="All">All Categories</option>
                  {PROFESSION_CATEGORIES.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Type:</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="filter-select"
                >
                  <option value="All">All</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Non-YouTube">Non-YouTube</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Filter by Status:</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="All">All Status</option>
                  <option value="Published">Published</option>
                  <option value="Draft">Draft</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Filter by Source:</label>
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="filter-select"
                >
                  <option value="All">All Sources</option>
                  {/* Build unique, normalized source list */}
                  {Object.entries(
                    articles
                      .filter(a => a.source)
                      .reduce((acc, a) => {
                        const key = normalizeSource(a.source);
                        if (key && !acc[key]) acc[key] = a.source.trim();
                        return acc;
                      }, {})
                  )
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Filter by Language:</label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="filter-select"
                >
                  <option value="All">All Languages</option>
                  {LANGUAGES.map(language => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group search-group">
                <label>Search:</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by title, author, or content..."
                  className="search-input"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="clear-search"
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Filter Summary */}
            {(selectedCategory !== 'All' || selectedStatus !== 'All' || selectedSource !== 'All' || selectedLanguage !== 'All' || searchTerm || showArchived) && (
              <div className="filter-summary">
                <span>Active filters:</span>
                {selectedCategory !== 'All' && <span className="filter-tag">Category: {selectedCategory}</span>}
                {selectedStatus !== 'All' && <span className="filter-tag">Status: {selectedStatus}</span>}
                {selectedSource !== 'All' && (
                  <span className="filter-tag">
                    Source: {(() => {
                      const map = articles.filter(a => a.source).reduce((acc, a) => {
                        const k = normalizeSource(a.source);
                        if (k && !acc[k]) acc[k] = a.source.trim();
                        return acc;
                      }, {});
                      return map[selectedSource] || selectedSource;
                    })()}
                  </span>
                )}
                {selectedLanguage !== 'All' && <span className="filter-tag">Language: {selectedLanguage}</span>}
                {searchTerm && <span className="filter-tag">Search: "{searchTerm}"</span>}
                {showArchived && <span className="filter-tag">Including Archived</span>}
                <button
                  onClick={() => {
                    setSelectedCategory('All');
                    setSelectedStatus('All');
                    setSelectedSource('All');
                    setSelectedLanguage('All');
                    setSearchTerm('');
                    setShowArchived(false);
                  }}
                  className="clear-filters"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
          
          {loading ? (
            <div className="loading">Loading articles...</div>
          ) : (
            <div className="articles-table">
              {filteredArticles.map(article => (
                <div key={article.id} className="article-row">
                  <div className="article-info">
                    <h3>{article.title}</h3>
                    <p className="article-meta">
                      <span>By {article.author}</span>
                      {article.source && <span>Source: {article.source}</span>}
                      <span>{article.category}</span>
                      <span>🌐 {article.language}</span>
                      <span>{article.pe_points_value} PE Points</span>
                      <span className={`status ${article.archived ? 'archived' : article.is_published ? 'published' : 'draft'}`}>
                        {article.archived ? 'Archived' : article.is_published ? 'Published' : 'Draft'}
                      </span>
                      {article.pdf_file_path && <span className="pdf-indicator">📄 PDF</span>}
                      {article.external_url && <span className="pdf-indicator" style={{ background: '#007bff' }}>🔗 Link</span>}
                    </p>
                    <p className="article-summary">{article.summary}</p>
                  </div>
                  <div className="article-actions">
                    <button 
                      className="btn btn-edit"
                      onClick={() => navigate(`/edit-article/${article.id}`)}
                      disabled={article.archived}
                      style={{ opacity: article.archived ? 0.6 : 1 }}
                    >
                      ✏️ Edit
                    </button>
                    {article.archived ? (
                      <button 
                        className="btn btn-success"
                        onClick={() => handleArchiveArticle(article.id, false)}
                        title="Restore from archive"
                      >
                        📤 Unarchive
                      </button>
                    ) : (
                      <button 
                        className="btn btn-warning"
                        onClick={() => handleArchiveArticle(article.id, true)}
                        title="Move to archive"
                        style={{ background: '#ffc107', borderColor: '#ffc107', color: '#212529' }}
                      >
                        📁 Archive
                      </button>
                    )}
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDeleteArticle(article.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
              
              {filteredArticles.length === 0 && !loading && (
                <div className="no-articles">
                  {articles.length === 0 ? (
                    <p>No articles found. Create your first article!</p>
                  ) : (
                    <p>No articles match your current filters. Try adjusting your search criteria.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {location.pathname === '/external-links' && (
          <div className="admin-section card">
            <div className="section-header">
              <h2>External Link Checker</h2>
              <button className="btn" onClick={runLinkCheck} disabled={linkCheckLoading}>
                {linkCheckLoading ? 'Running…' : 'Run Check'}
              </button>
            </div>
            {!linkCheckResult ? (
              <p style={{marginTop:'1rem'}}>Click "Run Check" to scan all external article links.</p>
            ) : (
              <div className="link-check-results" style={{marginTop:'1rem'}}>
                <div className="summary" style={{marginBottom:'1rem'}}>
                  <strong>Total:</strong> {linkCheckResult.summary?.total || 0}
                  {' '}· <strong>OK:</strong> {linkCheckResult.summary?.ok || 0}
                  {' '}· <strong>Bad:</strong> {linkCheckResult.summary?.bad || 0}
                  <label style={{ marginLeft: 16, fontWeight: 500 }}>
                    <input type="checkbox" checked={showBadOnly} onChange={(e)=>setShowBadOnly(e.target.checked)} style={{ marginRight: 6 }} />
                    Show bad only
                  </label>
                </div>
                <div className="results-table" style={{overflowX:'auto'}}>
                  <table style={{width:'100%', borderCollapse:'collapse'}}>
                    <thead>
                      <tr>
                        <th style={{textAlign:'left', padding:'8px', borderBottom:'1px solid #eee'}}>Article</th>
                        <th style={{textAlign:'left', padding:'8px', borderBottom:'1px solid #eee'}}>URL</th>
                        <th style={{textAlign:'left', padding:'8px', borderBottom:'1px solid #eee'}}>Status</th>
                        <th style={{textAlign:'left', padding:'8px', borderBottom:'1px solid #eee'}}>OK</th>
                        <th style={{textAlign:'left', padding:'8px', borderBottom:'1px solid #eee'}}>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(linkCheckResult.results || []).filter(r => !showBadOnly || !r.ok).map((r, idx) => (
                        <tr key={idx} style={{ background: r.ok ? 'transparent' : '#fff5f5' }}>
                          <td style={{padding:'8px', borderBottom:'1px solid #f5f5f5'}}>{r.title} (#{r.article_id})</td>
                          <td style={{padding:'8px', borderBottom:'1px solid #f5f5f5'}}>
                            <a href={r.url} target="_blank" rel="noreferrer">{r.url}</a>
                          </td>
                          <td style={{padding:'8px', borderBottom:'1px solid #f5f5f5'}}>{r.status ?? '—'}</td>
                          <td style={{padding:'8px', borderBottom:'1px solid #f5f5f5'}}>
                            {r.ok ? (
                              <span style={{background:'#e6ffed', color:'#0f5132', padding:'2px 8px', borderRadius:999}}>OK</span>
                            ) : (
                              <span style={{background:'#fff5f5', color:'#842029', padding:'2px 8px', borderRadius:999}}>BAD</span>
                            )}
                          </td>
                          <td style={{padding:'8px', borderBottom:'1px solid #f5f5f5', color: r.ok ? '#666' : '#c00'}}>{r.error || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {location.pathname === '/youtube-videos' && (
          <div className="admin-section card">
            <div className="section-header">
              <h2>YouTube Videos</h2>
            </div>
            <div style={{padding:'1rem'}}>
              <label style={{fontWeight:600, display:'block', marginBottom:8}}>YouTube URL</label>
              <div style={{display:'flex', gap:8}}>
                <input
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={youtubeUrl}
                  onChange={(e)=>setYoutubeUrl(e.target.value)}
                  style={{flex:1, padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}
                />
                <button className="btn" onClick={validateYouTube} disabled={ytLoading}>
                  {ytLoading ? 'Checking…' : 'Check Embeddable'}
                </button>
              </div>
              {ytError && <div style={{marginTop:8, color:'#c00'}}>{ytError}</div>}
              {ytEmbeddable && ytVideoId && (
                <div style={{marginTop:16}}>
                  <div style={{marginBottom:8, color:'#0f5132'}}>Embeddable ✓</div>
                  <label style={{fontWeight:600, display:'block', marginBottom:8}}>Title</label>
                  <input
                    type="text"
                    value={ytTitle}
                    onChange={(e)=>setYtTitle(e.target.value)}
                    placeholder="Enter video title"
                    style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6, marginBottom:12}}
                  />
                  <div style={{
                    marginTop:8,
                    padding:'10px 12px',
                    background:'#fff8e1',
                    border:'1px solid #ffe58f',
                    borderRadius:6,
                    color:'#8d6e63'
                  }}>
                    Preview hidden in admin. The video will be embedded on the user dashboard.
                    {' '}
                    <a href={`https://www.youtube.com/watch?v=${ytVideoId}`} target="_blank" rel="noreferrer">Open on YouTube</a>
                  </div>

                  {/* Manual Questions */}
                  <div style={{marginTop:16}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                      <h3 style={{margin:0}}>Questions</h3>
                      <button className="btn" onClick={addYtQuestion}>+ Add Question</button>
                    </div>
                    {ytQuestions.length === 0 && (
                      <div style={{marginTop:8, color:'#6b7280'}}>No questions yet.</div>
                    )}
                    {ytQuestions.map((q, idx) => (
                      <div key={idx} style={{border:'1px solid #e5e7eb', borderRadius:8, padding:'12px', marginTop:10}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                          <strong>Question {idx+1}</strong>
                          <button className="btn" onClick={()=>removeYtQuestion(idx)}>Remove</button>
                        </div>
                        <label style={{display:'block', fontWeight:600, marginBottom:4}}>Prompt</label>
                        <textarea value={q.prompt} onChange={(e)=>updateYtQuestion(idx,'prompt',e.target.value)} rows={2} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:8}}>
                          <div>
                            <label style={{fontWeight:600, display:'block'}}>Option A</label>
                            <input value={q.option_a} onChange={(e)=>updateYtQuestion(idx,'option_a',e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                          </div>
                          <div>
                            <label style={{fontWeight:600, display:'block'}}>Option B</label>
                            <input value={q.option_b} onChange={(e)=>updateYtQuestion(idx,'option_b',e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                          </div>
                          <div>
                            <label style={{fontWeight:600, display:'block'}}>Option C</label>
                            <input value={q.option_c} onChange={(e)=>updateYtQuestion(idx,'option_c',e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                          </div>
                          <div>
                            <label style={{fontWeight:600, display:'block'}}>Option D</label>
                            <input value={q.option_d} onChange={(e)=>updateYtQuestion(idx,'option_d',e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                          </div>
                        </div>
                        <div style={{marginTop:8}}>
                          <label style={{fontWeight:600, marginRight:8}}>Correct:</label>
                          {['A','B','C','D'].map(opt => (
                            <label key={opt} style={{marginRight:12}}>
                              <input type="radio" name={`correct_${idx}`} checked={q.correct===opt} onChange={()=>updateYtQuestion(idx,'correct',opt)} /> {opt}
                            </label>
                          ))}
                        </div>
                        <div style={{marginTop:8}}>
                          <label style={{display:'block', fontWeight:600, marginBottom:4}}>Explanation (why the correct option is right)</label>
                          <textarea value={q.explanation || ''} onChange={(e)=>updateYtQuestion(idx,'explanation',e.target.value)} rows={2} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                        </div>
                      </div>
                    ))}
                    {ytQuestions.length > 0 && (
                      <div style={{marginTop:12}}>
                        <button className="btn" onClick={persistYtDraft}>Save Draft (Local)</button>
                      </div>
                    )}
                  </div>

                  {/* Publish Meta */}
                  <div style={{marginTop:20}}>
                    <h3 style={{margin:'8px 0'}}>Publish Settings</h3>
                    <div style={{marginBottom:12}}>
                      <label style={{fontWeight:600, display:'block', marginBottom:6}}>Summary</label>
                      <textarea
                        rows={3}
                        value={ytSummary}
                        onChange={(e)=>setYtSummary(e.target.value)}
                        placeholder="Short summary users will see on the card"
                        style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}
                      />
                    </div>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>Category</label>
                        <select value={ytCategory} onChange={(e)=>setYtCategory(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                          <option value="">Select Category</option>
                          {PROFESSION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>Secondary Category (optional)</label>
                        <select value={ytSecondaryCategory} onChange={(e)=>setYtSecondaryCategory(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                          <option value="">None</option>
                          {PROFESSION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>Language</label>
                        <select value={ytLanguage} onChange={(e)=>setYtLanguage(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                          {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>PE Points</label>
                        <select value={ytPePoints} onChange={(e)=>setYtPePoints(parseFloat(e.target.value))} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                          {PE_POINTS_VALUES.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{fontWeight:600, display:'block'}}>Difficulty</label>
                        <select value={ytDifficulty} onChange={(e)=>setYtDifficulty(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                          {DIFFICULTY_LEVELS.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{marginTop:12}}>
                      <button className="btn" onClick={publishYouTubeAsArticle} disabled={ytPublishing}>
                        {ytPublishing ? 'Publishing…' : 'Publish to Users'}
                      </button>
                      <button className="btn" style={{marginLeft:8}} onClick={persistYtDraft}>Save Draft</button>
                    </div>
                  </div>
                </div>
              )}
              {ytEmbeddable === false && (
                <div style={{marginTop:8, color:'#842029'}}>Not embeddable ✗</div>
              )}
            </div>
          </div>
        )}

        {location.pathname === '/podcasts' && (
          <div className="admin-section card">
            <div className="section-header">
              <h2>Podcasts</h2>
            </div>
            <div style={{padding:'1rem'}}>
              <label style={{fontWeight:600, display:'block', marginBottom:8}}>Podcast URL</label>
              <div style={{display:'flex', gap:8}}>
                <input
                  type="url"
                  placeholder="Spotify/Apple/SoundCloud/Anchor URL"
                  value={podUrl}
                  onChange={(e)=>setPodUrl(e.target.value)}
                  style={{flex:1, padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}
                />
                <button className="btn" onClick={validatePodcast} disabled={podLoading}>
                  {podLoading ? 'Checking…' : 'Build Embed'}
                </button>
              </div>
              {podError && <div style={{marginTop:8, color:'#c00'}}>{podError}</div>}
              {podEmbedUrl && (
                <div style={{marginTop:16}}>
                  <div style={{marginBottom:8, color:'#0f5132'}}>Detected: {podPlatform}</div>
                  <div style={{position:'relative', paddingBottom:'30%', height:0, overflow:'hidden', border:'1px solid #e5e7eb', borderRadius:8}}>
                    <iframe
                      title={podTitle || 'Podcast'}
                      src={podEmbedUrl}
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                    />
                  </div>
                </div>
              )}

              <div style={{marginTop:16}}>
                <label style={{fontWeight:600, display:'block', marginBottom:6}}>Title</label>
                <input type="text" value={podTitle} onChange={(e)=>setPodTitle(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
              </div>
              <div style={{marginTop:12}}>
                <label style={{fontWeight:600, display:'block', marginBottom:6}}>Summary</label>
                <textarea rows={3} value={podSummary} onChange={(e)=>setPodSummary(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:12}}>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Category</label>
                  <select value={podCategory} onChange={(e)=>setPodCategory(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    <option value="">Select Category</option>
                    {PROFESSION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Watch Time (minutes)</label>
                  <input type="number" min="1" step="1" value={podWatchTime ?? ''} onChange={(e)=>setPodWatchTime(e.target.value)} placeholder="e.g., 10" style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}} />
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Secondary Category (optional)</label>
                  <select value={podSecondaryCategory} onChange={(e)=>setPodSecondaryCategory(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    <option value="">None</option>
                    {PROFESSION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Language</label>
                  <select value={podLanguage} onChange={(e)=>setPodLanguage(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>PE Points</label>
                  <select value={podPePoints} onChange={(e)=>setPodPePoints(parseFloat(e.target.value))} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    {PE_POINTS_VALUES.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontWeight:600, display:'block'}}>Difficulty</label>
                  <select value={podDifficulty} onChange={(e)=>setPodDifficulty(e.target.value)} style={{width:'100%', padding:'0.6rem', border:'1px solid #d1d5db', borderRadius:6}}>
                    {DIFFICULTY_LEVELS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div className="card" style={{ marginTop: 16, padding: 12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <h3 style={{ margin:0 }}>Questions</h3>
                  <button className="btn" onClick={()=>setPodQuestions([...(podQuestions||[]), { prompt:'', option_a:'', option_b:'', option_c:'', option_d:'', correct_answer:'A' }])}>+ Add question</button>
                </div>
                {(podQuestions||[]).length === 0 && <div style={{marginTop:8, color:'#6b7280'}}>No questions yet.</div>}
                {(podQuestions||[]).map((q, idx)=> (
                  <div key={idx} style={{ marginTop:12, borderTop:'1px solid #eee', paddingTop:12 }}>
                    <input style={{ width:'100%', marginBottom:8 }} placeholder={`Question ${idx+1}`} value={q.prompt} onChange={(e)=>{
                      const next=[...podQuestions]; next[idx]={...q, prompt:e.target.value}; setPodQuestions(next);
                    }} />
                    <div className="grid-2">
                      {['A','B','C','D'].map(letter=> (
                        <input key={letter} placeholder={`Option ${letter}`} value={q[`option_${letter.toLowerCase()}`]||''} onChange={(e)=>{
                          const next=[...podQuestions]; next[idx]={...q, [`option_${letter.toLowerCase()}`]:e.target.value}; setPodQuestions(next);
                        }} />
                      ))}
                    </div>
                    <div style={{ marginTop:8 }}>
                      <label>Correct</label>
                      <select value={q.correct_answer} onChange={(e)=>{ const next=[...podQuestions]; next[idx]={...q, correct_answer:e.target.value}; setPodQuestions(next); }}>
                        {['A','B','C','D'].map(letter=> <option key={letter} value={letter}>{letter}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16 }}>
                <button className="btn btn-primary" onClick={publishPodcastAsArticle} disabled={podPublishing || !podUrl}>Publish</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Route Components
function AddArticlePage({ user, handleLogout, handleApiAuthFailure }) {
  return <AdminDashboard user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />;
}

function EditArticlePage({ user, handleLogout, handleApiAuthFailure }) {
  return <AdminDashboard user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />;
}

function DashboardPage({ user, handleLogout, handleApiAuthFailure }) {
  return <AdminDashboard user={user} handleLogout={handleLogout} handleApiAuthFailure={handleApiAuthFailure} />;
}

// News Management Page Component
function NewsManagementPage({ user, handleLogout, handleApiAuthFailure }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'news'
  });

  // Fetch news items
  const fetchNews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/news`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNewsItems(data);
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        console.error('Error fetching news:', response.status);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create or update news item
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      const url = editingNews 
        ? `${API_BASE_URL}/admin/news/${editingNews.id}`
        : `${API_BASE_URL}/admin/news`;
      
      const method = editingNews ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchNews();
        setShowForm(false);
        setEditingNews(null);
        setFormData({ title: '', content: '', type: 'news' });
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        console.error('Error saving news:', response.status);
      }
    } catch (error) {
      console.error('Error saving news:', error);
    }
  };

  // Delete news item
  const handleDelete = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this news item?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/news/${newsId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchNews();
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        console.error('Error deleting news:', response.status);
      }
    } catch (error) {
      console.error('Error deleting news:', error);
    }
  };

  // Edit news item
  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      type: newsItem.type
    });
    setShowForm(true);
  };

  // Cancel form
  const handleCancel = () => {
    setShowForm(false);
    setEditingNews(null);
    setFormData({ title: '', content: '', type: 'news' });
  };

  // Load news on component mount
  useEffect(() => {
    fetchNews();
  }, []);

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-brand" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
          <h2>Protagon - Admin Dashboard</h2>
        </div>
        <div className="nav-content">
          <div className="nav-info">
            <span>Welcome, {user.first_name} {user.last_name}</span>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Add News Item'}
          </button>
        </div>
      </nav>

      <main className="main-content">
        <div className="admin-header">
          <div className="admin-nav">
            <button 
              className="nav-tab"
              onClick={() => navigate('/dashboard')}
            >
              📚 Articles Management
            </button>
            <button className="nav-tab active">
              📰 News Management
            </button>
            <button 
              className="nav-tab"
              onClick={() => navigate('/feedback-management')}
            >
              📝 Feedback Management
            </button>
            <button 
              className="nav-tab"
              onClick={() => navigate('/discount-codes')}
            >
              🎟️ Discount Codes
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/article-requests' ? 'active' : ''}`}
              onClick={() => navigate('/article-requests')}
            >
              📝 Article Requests
            </button>
          </div>
          
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Add News Item'}
          </button>
        </div>

        {showForm && (
          <div className="add-article-form">
            <h2>{editingNews ? 'Edit News Item' : 'Add News Item'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    placeholder="Enter news title..."
                  />
                </div>
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    required
                  >
                    <option value="news">📰 News</option>
                    <option value="feature">🔵 Feature</option>
                    <option value="announcement">🟢 Announcement</option>
                    <option value="update">🟡 Update</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Content *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  required
                  rows="6"
                  placeholder="Enter news content..."
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingNews ? 'Update News' : 'Create News'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="articles-section">
          <h2>News Items</h2>
          
          {loading ? (
            <div className="loading">Loading news items...</div>
          ) : newsItems.length === 0 ? (
            <div className="no-articles">
              <p>No news items found. Create your first news item to get started!</p>
            </div>
          ) : (
            <div className="articles-table">
              {newsItems.map(item => (
                <div key={item.id} className="article-row">
                  <div className="article-info">
                    <h3>{item.title}</h3>
                    <p className="article-meta">
                      <span className={`status ${item.type}`}>
                        {item.type === 'feature' && '🔵 Feature'}
                        {item.type === 'announcement' && '🟢 Announcement'}
                        {item.type === 'update' && '🟡 Update'}
                        {item.type === 'news' && '📰 News'}
                      </span>
                      <span>Created: {new Date(item.created_at).toLocaleDateString()}</span>
                      <span>By: {item.creator?.first_name} {item.creator?.last_name}</span>
                      <span className={`status ${item.is_active ? 'published' : 'draft'}`}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                    <p className="article-summary">{item.content}</p>
                  </div>
                  <div className="article-actions">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => handleEdit(item)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Feedback Management Page Component
function FeedbackManagementPage({ user, handleLogout, handleApiAuthFailure }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [feedbackSummary, setFeedbackSummary] = useState([]);
  const [selectedArticleFeedback, setSelectedArticleFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Fetch feedback summary for all articles
  const fetchFeedbackSummary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/articles/feedback/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFeedbackSummary(data.articles_feedback_summary);
      } else if (response.status === 401) {
        handleApiAuthFailure();
      } else {
        alert('Failed to fetch feedback summary');
      }
    } catch (error) {
      console.error('Error fetching feedback summary:', error);
      alert('Error fetching feedback summary');
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed feedback for a specific article
  const fetchArticleFeedback = async (articleId, articleTitle) => {
    try {
      setDetailsLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/articles/${articleId}/feedback`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedArticleFeedback(data);
      } else if (response.status === 401) {
        handleApiAuthFailure();
      } else {
        alert('Failed to fetch article feedback');
      }
    } catch (error) {
      console.error('Error fetching article feedback:', error);
      alert('Error fetching article feedback');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Get rating color based on average rating
  const getRatingColor = (rating) => {
    if (rating >= 4.5) return '#4CAF50'; // Green
    if (rating >= 4.0) return '#8BC34A'; // Light Green
    if (rating >= 3.5) return '#FFC107'; // Yellow
    if (rating >= 3.0) return '#FF9800'; // Orange
    if (rating >= 2.0) return '#FF5722'; // Deep Orange
    return '#F44336'; // Red
  };

  // Render star rating display
  const renderStarRating = (rating, size = '16px') => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {Array(fullStars).fill().map((_, i) => (
          <span key={`full-${i}`} style={{ color: '#FFD700', fontSize: size }}>★</span>
        ))}
        {halfStar && <span style={{ color: '#FFD700', fontSize: size }}>☆</span>}
        {Array(emptyStars).fill().map((_, i) => (
          <span key={`empty-${i}`} style={{ color: '#DDD', fontSize: size }}>★</span>
        ))}
        <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
          ({rating.toFixed(1)})
        </span>
      </span>
    );
  };

  // Load feedback summary on component mount
  useEffect(() => {
    fetchFeedbackSummary();
  }, []);

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-brand" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
          <h2>Protagon - Admin Dashboard</h2>
        </div>
        <div className="nav-content">
          <div className="nav-info">
            <span>Welcome, {user.first_name} {user.last_name}</span>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => {
              const adminToken = localStorage.getItem('admin_token');
              const userDashboardUrl = `https://cpd-pro.app/?admin_token=${adminToken}&user_data=${encodeURIComponent(JSON.stringify(user))}`;
              window.open(userDashboardUrl, '_blank');
            }}
            style={{
              marginRight: '1rem',
              background: '#4CAF50',
              borderColor: '#4CAF50'
            }}
          >
            🚀 Access User Dashboard
          </button>
          <button className="btn btn-secondary logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </nav>

      <main className="main-content">
        <div className="admin-header">
          <div className="admin-nav">
            <button 
              className="nav-tab"
              onClick={() => navigate('/dashboard')}
            >
              📚 Articles Management
            </button>
            <button 
              className="nav-tab"
              onClick={() => navigate('/news-management')}
            >
              📰 News Management
            </button>
            <button className="nav-tab active">
              📝 Feedback Management
            </button>
            <button 
              className="nav-tab"
              onClick={() => navigate('/discount-codes')}
            >
              🎟️ Discount Codes
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/article-requests' ? 'active' : ''}`}
              onClick={() => navigate('/article-requests')}
            >
              📝 Article Requests
            </button>
          </div>
        </div>

        <div className="feedback-management-section">
          <div className="feedback-header">
            <h2>Article Feedback Analytics</h2>
            <p>Monitor user feedback and ratings for all articles</p>
          </div>

          {/* Feedback Summary Table */}
          {loading ? (
            <div className="loading-state">Loading feedback data...</div>
          ) : (
            <div className="feedback-summary-table">
              <h3>Feedback Summary ({feedbackSummary.length} articles)</h3>
              
              {feedbackSummary.length === 0 ? (
                <div className="no-feedback">
                  <p>No feedback has been submitted yet.</p>
                  <p>Users can submit feedback after reading articles.</p>
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Article Title</th>
                      <th>Average Rating</th>
                      <th>Total Feedback</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbackSummary.map((article) => (
                      <tr key={article.article_id}>
                        <td className="article-title-cell">
                          <strong>{article.article_title}</strong>
                        </td>
                        <td className="rating-cell">
                          {article.total_feedback > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {renderStarRating(article.average_rating)}
                              <span style={{ 
                                background: getRatingColor(article.average_rating),
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                {article.average_rating.toFixed(1)}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>No ratings yet</span>
                          )}
                        </td>
                        <td className="feedback-count-cell">
                          <span style={{
                            background: article.total_feedback > 0 ? '#4CAF50' : '#999',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {article.total_feedback}
                          </span>
                        </td>
                        <td className="actions-cell">
                          {article.total_feedback > 0 ? (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => fetchArticleFeedback(article.article_id, article.article_title)}
                            >
                              View Details
                            </button>
                          ) : (
                            <span style={{ color: '#999', fontSize: '12px' }}>No feedback</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Detailed Feedback Modal/Section */}
          {selectedArticleFeedback && (
            <div className="feedback-details-modal">
              <div className="feedback-details-content">
                <div className="feedback-details-header">
                  <h3>Feedback for: {selectedArticleFeedback.article_title}</h3>
                  <button 
                    className="close-btn"
                    onClick={() => setSelectedArticleFeedback(null)}
                  >
                    ✕
                  </button>
                </div>

                {detailsLoading ? (
                  <div className="loading-state">Loading detailed feedback...</div>
                ) : (
                  <div className="feedback-details-body">
                    {/* Statistics Summary */}
                    <div className="feedback-stats">
                      <div className="stat-card">
                        <h4>Average Rating</h4>
                        <div className="stat-value">
                          {renderStarRating(selectedArticleFeedback.average_rating, '20px')}
                        </div>
                      </div>
                      <div className="stat-card">
                        <h4>Total Feedback</h4>
                        <div className="stat-value">{selectedArticleFeedback.total_feedback}</div>
                      </div>
                    </div>

                    {/* Rating Distribution */}
                    <div className="rating-distribution">
                      <h4>Rating Distribution</h4>
                      {[5, 4, 3, 2, 1].map(rating => {
                        const count = selectedArticleFeedback.rating_distribution[rating.toString()];
                        const percentage = selectedArticleFeedback.total_feedback > 0 
                          ? (count / selectedArticleFeedback.total_feedback * 100).toFixed(1)
                          : 0;
                        
                        return (
                          <div key={rating} className="rating-bar">
                            <span className="rating-label">{rating} ★</span>
                            <div className="rating-bar-container">
                              <div 
                                className="rating-bar-fill"
                                style={{ 
                                  width: `${percentage}%`,
                                  backgroundColor: getRatingColor(rating)
                                }}
                              />
                            </div>
                            <span className="rating-count">{count} ({percentage}%)</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Individual Feedback Items */}
                    <div className="individual-feedback">
                      <h4>Individual Feedback ({selectedArticleFeedback.feedback.length})</h4>
                      <div className="feedback-list">
                        {selectedArticleFeedback.feedback.map((feedback) => (
                          <div key={feedback.id} className="feedback-item">
                            <div className="feedback-header">
                              <div className="feedback-user">
                                <span className="user-avatar">
                                  {feedback.user_initial}
                                </span>
                                <span className="user-name">{feedback.user_name}</span>
                              </div>
                              <div className="feedback-rating">
                                {renderStarRating(feedback.rating)}
                              </div>
                              <div className="feedback-date">
                                {new Date(feedback.created_at).toLocaleDateString()}
                              </div>
                            </div>
                            {feedback.comment && (
                              <div className="feedback-comment">
                                <p>"{feedback.comment}"</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <style jsx>{`
        .feedback-management-section {
          padding: 2rem;
        }

        .feedback-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .feedback-header h2 {
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }

        .feedback-summary-table {
          background: white;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-bottom: 2rem;
        }

        .feedback-summary-table h3 {
          margin-top: 0;
          color: #2c3e50;
        }

        .admin-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1rem;
        }

        .admin-table th,
        .admin-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }

        .admin-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #2d3748;
        }

        .article-title-cell {
          max-width: 300px;
        }

        .rating-cell {
          min-width: 150px;
        }

        .feedback-count-cell,
        .actions-cell {
          text-align: center;
          min-width: 100px;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 12px;
        }

        .no-feedback {
          text-align: center;
          padding: 3rem;
          color: #666;
        }

        .loading-state {
          text-align: center;
          padding: 2rem;
          color: #666;
        }

        /* Modal Styles */
        .feedback-details-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .feedback-details-content {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }

        .feedback-details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .feedback-details-header h3 {
          margin: 0;
          color: #2c3e50;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #666;
          padding: 4px;
        }

        .close-btn:hover {
          color: #333;
        }

        .feedback-details-body {
          padding: 1.5rem;
        }

        .feedback-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: #f8f9fa;
          padding: 1rem;
          border-radius: 8px;
          text-align: center;
        }

        .stat-card h4 {
          margin: 0 0 0.5rem 0;
          color: #666;
          font-size: 14px;
        }

        .stat-value {
          font-size: 18px;
          font-weight: bold;
          color: #2c3e50;
        }

        .rating-distribution {
          margin-bottom: 2rem;
        }

        .rating-distribution h4 {
          margin-bottom: 1rem;
          color: #2c3e50;
        }

        .rating-bar {
          display: grid;
          grid-template-columns: 40px 1fr 80px;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }

        .rating-label {
          font-size: 14px;
          color: #666;
        }

        .rating-bar-container {
          height: 20px;
          background: #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
        }

        .rating-bar-fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .rating-count {
          font-size: 12px;
          color: #666;
          text-align: right;
        }

        .individual-feedback h4 {
          margin-bottom: 1rem;
          color: #2c3e50;
        }

        .feedback-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .feedback-item {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
          border-left: 4px solid #4CAF50;
        }

        .feedback-header {
          display: grid;
          grid-template-columns: 1fr auto auto;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.5rem;
        }

        .feedback-user {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #4CAF50;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
        }

        .user-name {
          font-weight: 500;
          color: #2c3e50;
        }

        .feedback-date {
          font-size: 12px;
          color: #666;
        }

        .feedback-comment {
          background: white;
          padding: 0.75rem;
          border-radius: 6px;
          border-left: 3px solid #2196F3;
        }

        .feedback-comment p {
          margin: 0;
          color: #2c3e50;
          font-style: italic;
          line-height: 1.4;
        }

        @media (max-width: 768px) {
          .feedback-details-modal {
            padding: 1rem;
          }

          .feedback-stats {
            grid-template-columns: 1fr;
          }

          .rating-bar {
            grid-template-columns: 30px 1fr 60px;
          }

          .feedback-header {
            grid-template-columns: 1fr;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}

// Discount Codes Page Component
function DiscountCodesPage({ user, handleLogout, handleApiAuthFailure }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    max_uses_per_user: '',
    valid_from: '',
    valid_until: '',
    applicable_plans: [],
    minimum_amount: '',
    is_active: true,
    create_stripe_coupon: true
  });
  const [usageStats, setUsageStats] = useState([]);
  const [activeTab, setActiveTab] = useState('codes'); // 'codes' or 'usage'

  // Available subscription plans
  const SUBSCRIPTION_PLANS = [
    { id: 'price_1TrMOMIkwUVtcv9M3cZ3qhGX', name: 'Professional Plan' },
    { id: 'price_1TrMaeIkwUVtcv9MDRqJUJ0N', name: 'Professional Monthly' }
  ];

  // Fetch discount codes
  const fetchDiscountCodes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/discount-codes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDiscountCodes(data);
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        console.error('Error fetching discount codes:', response.status);
      }
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch usage statistics
  const fetchUsageStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/discount-usage`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        console.error('Error fetching usage stats:', response.status);
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  };

  // Create or update discount code
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      // Prepare form data
      const submitData = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: formData.max_uses_per_user ? parseInt(formData.max_uses_per_user) : null,
        minimum_amount: formData.minimum_amount ? parseInt(formData.minimum_amount * 100) : null, // Convert to cents
        applicable_plans: formData.applicable_plans.length > 0 ? formData.applicable_plans : null
      };

      // Debug logging
      console.log('Submitting discount code data:', {
        original_max_uses_per_user: formData.max_uses_per_user,
        parsed_max_uses_per_user: submitData.max_uses_per_user,
        full_submitData: submitData
      });

      const url = editingCode 
        ? `${API_BASE_URL}/admin/discount-codes/${editingCode.id}`
        : `${API_BASE_URL}/admin/discount-codes`;
      
      const method = editingCode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        // First close the form and clear editing state
        setShowForm(false);
        setEditingCode(null);
        
        // Refresh the discount codes data 
        await fetchDiscountCodes();
        
        // Reset form data to defaults only after closing the form
        setFormData({
          code: '',
          description: '',
          discount_type: 'percentage',
          discount_value: '',
          max_uses: '',
          max_uses_per_user: '',
          valid_from: '',
          valid_until: '',
          applicable_plans: [],
          minimum_amount: '',
          is_active: true,
          create_stripe_coupon: true
        });
        
        alert(editingCode ? 'Discount code updated successfully!' : 'Discount code created successfully!');
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error saving discount code:', error);
      alert('Error saving discount code');
    }
  };

  // Delete discount code
  const handleDelete = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/discount-codes/${codeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchDiscountCodes();
        alert('Discount code deleted successfully!');
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error deleting discount code:', error);
      alert('Error deleting discount code');
    }
  };

  // Edit discount code
  const handleEdit = (code) => {
    console.log('Editing discount code:', {
      id: code.id,
      code: code.code,
      max_uses_per_user: code.max_uses_per_user,
      full_code_object: code
    });

    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description || '',
      discount_type: code.discount_type,
      discount_value: code.discount_value !== null ? code.discount_value.toString() : '',
      max_uses: code.max_uses ? code.max_uses.toString() : '',
      max_uses_per_user: code.max_uses_per_user !== null && code.max_uses_per_user !== undefined ? code.max_uses_per_user.toString() : '',
      valid_from: code.valid_from ? code.valid_from.split('T')[0] : '',
      valid_until: code.valid_until ? code.valid_until.split('T')[0] : '',
      applicable_plans: code.applicable_plans ? JSON.parse(code.applicable_plans) : [],
      minimum_amount: code.minimum_amount ? (code.minimum_amount / 100).toString() : '',
      is_active: code.is_active,
      create_stripe_coupon: false // Don't recreate Stripe coupon when editing
    });
    
    console.log('Form data set to:', {
      max_uses_per_user: code.max_uses_per_user !== null && code.max_uses_per_user !== undefined ? code.max_uses_per_user.toString() : 'null/blank'
    });
    
    setShowForm(true);
  };

  // Cancel form
  const handleCancel = () => {
    setShowForm(false);
    setEditingCode(null);
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      max_uses: '',
      max_uses_per_user: '', // Default to blank for unlimited
      valid_from: '',
      valid_until: '',
      applicable_plans: [],
      minimum_amount: '',
      is_active: true,
      stripe_coupon_id: ''
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle multi-select for applicable plans
  const handlePlanToggle = (planId) => {
    setFormData(prev => ({
      ...prev,
      applicable_plans: prev.applicable_plans.includes(planId)
        ? prev.applicable_plans.filter(id => id !== planId)
        : [...prev.applicable_plans, planId]
    }));
  };

  // Format currency
  const formatCurrency = (cents) => {
    return `€${(cents / 100).toFixed(2)}`;
  };

  // Load data on component mount
  useEffect(() => {
    fetchDiscountCodes();
    fetchUsageStats();
  }, []);

  const handleResetUsage = async (codeId, codeName) => {
    if (!window.confirm(`Are you sure you want to reset all usage records for discount code "${codeName}"? This will clear all user usage history for this code.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/discount-codes/${codeId}/reset-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        handleApiAuthFailure();
        return;
      }

      const result = await response.json();

      if (response.ok) {
        alert(`${result.message}`);
        // Refresh the discount codes list
        fetchDiscountCodes();
      } else {
        alert(result.error || 'Failed to reset usage records');
      }
    } catch (error) {
      console.error('Reset usage error:', error);
      alert('An error occurred while resetting usage records');
    }
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-brand" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
          <h2>Protagon - Admin Dashboard</h2>
        </div>
        <div className="nav-content">
          <div className="nav-info">
            <span>Welcome, {user.first_name} {user.last_name}</span>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => {
              const adminToken = localStorage.getItem('admin_token');
              const userDashboardUrl = `https://cpd-pro.app/?admin_token=${adminToken}&user_data=${encodeURIComponent(JSON.stringify(user))}`;
              window.open(userDashboardUrl, '_blank');
            }}
            style={{
              marginRight: '1rem',
              background: '#4CAF50',
              borderColor: '#4CAF50'
            }}
          >
            🚀 Access User Dashboard
          </button>
          <button className="btn btn-secondary logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </nav>

      <main className="main-content">
        <div className="admin-header">
          <div className="admin-nav">
            <button 
              className="nav-tab"
              onClick={() => navigate('/dashboard')}
            >
              📚 Articles Management
            </button>
            <button 
              className="nav-tab"
              onClick={() => navigate('/news-management')}
            >
              📰 News Management
            </button>
            <button 
              className="nav-tab"
              onClick={() => navigate('/feedback-management')}
            >
              📝 Feedback Management
            </button>
            <button className="nav-tab active">
              🎟️ Discount Codes
            </button>
            <button 
              className={`nav-tab ${location.pathname === '/article-requests' ? 'active' : ''}`}
              onClick={() => navigate('/article-requests')}
            >
              📝 Article Requests
            </button>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Add Discount Code'}
          </button>
        </div>

        <div className="discount-codes-section">
          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button 
              className={`tab-btn ${activeTab === 'codes' ? 'active' : ''}`}
              onClick={() => setActiveTab('codes')}
            >
              Discount Codes ({discountCodes.length})
            </button>
            <button 
              className={`tab-btn ${activeTab === 'usage' ? 'active' : ''}`}
              onClick={() => setActiveTab('usage')}
            >
              Usage Statistics ({usageStats.length})
            </button>
          </div>

          {/* Add/Edit Form */}
          {showForm && (
            <div className="add-article-form">
              <h2>{editingCode ? 'Edit Discount Code' : 'Add Discount Code'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Code *</label>
                    <input
                      type="text"
                      name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., SAVE20"
                    style={{ textTransform: 'uppercase' }}
                    disabled={editingCode} // Can't change code when editing
                  />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Brief description of the discount"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Discount Type *</label>
                    <select
                      name="discount_type"
                      value={formData.discount_type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount (€)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Discount Value *</label>
                    <input
                      type="number"
                      name="discount_value"
                      value={formData.discount_value}
                      onChange={handleInputChange}
                      required
                      min="0"
                      max={formData.discount_type === 'percentage' ? '100' : undefined}
                      step={formData.discount_type === 'percentage' ? '0.1' : '0.01'}
                      placeholder={formData.discount_type === 'percentage' ? '20' : '50.00'}
                    />
                    <small>{formData.discount_type === 'percentage' ? '(0-100%)' : '(Amount in €)'}</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Maximum Uses</label>
                    <input
                      type="number"
                      name="max_uses"
                      value={formData.max_uses}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="Leave empty for unlimited"
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Uses per User</label>
                    <input
                      type="number"
                      name="max_uses_per_user"
                      value={formData.max_uses_per_user}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="Leave blank for unlimited"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Valid From</label>
                    <input
                      type="date"
                      name="valid_from"
                      value={formData.valid_from}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Valid Until</label>
                    <input
                      type="date"
                      name="valid_until"
                      value={formData.valid_until}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Applicable Plans</label>
                  <div className="plan-selection">
                    {SUBSCRIPTION_PLANS.map(plan => (
                      <label key={plan.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.applicable_plans.includes(plan.id)}
                          onChange={() => handlePlanToggle(plan.id)}
                        />
                        {plan.name}
                      </label>
                    ))}
                  </div>
                  <small>Leave empty to apply to all plans</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Minimum Amount (€)</label>
                    <input
                      type="number"
                      name="minimum_amount"
                      value={formData.minimum_amount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      Active
                    </label>
                  </div>
                </div>

                {!editingCode && (
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="create_stripe_coupon"
                        checked={formData.create_stripe_coupon}
                        onChange={handleInputChange}
                      />
                      Create Stripe Coupon
                    </label>
                    <small>Creates a corresponding coupon in Stripe for automatic discount application</small>
                  </div>
                )}

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingCode ? 'Update Discount Code' : 'Create Discount Code'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Discount Codes Tab */}
          {activeTab === 'codes' && (
            <div className="codes-tab">
              <h2>Discount Codes</h2>
              
              {loading ? (
                <div className="loading">Loading discount codes...</div>
              ) : discountCodes.length === 0 ? (
                <div className="no-articles">
                  <p>No discount codes found. Create your first discount code to get started!</p>
                </div>
              ) : (
                <div className="codes-table">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Description</th>
                        <th>Discount</th>
                        <th>Usage</th>
                        <th>Validity</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discountCodes.map(code => (
                        <tr key={code.id}>
                          <td>
                            <strong>{code.code}</strong>
                            {code.stripe_coupon_id && (
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                Stripe: {code.stripe_coupon_id}
                              </div>
                            )}
                          </td>
                          <td>{code.description || '-'}</td>
                          <td>
                            {code.discount_type === 'percentage' 
                              ? `${code.discount_value}%` 
                              : formatCurrency(code.discount_value)
                            }
                          </td>
                          <td>
                            {code.current_uses} / {code.max_uses || '∞'}
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              Max per user: {code.max_uses_per_user}
                            </div>
                          </td>
                          <td>
                            {code.valid_from ? new Date(code.valid_from).toLocaleDateString() : 'No start'} - 
                            {code.valid_until ? new Date(code.valid_until).toLocaleDateString() : 'No end'}
                          </td>
                          <td>
                            <span className={`status ${code.is_active ? 'published' : 'draft'}`}>
                              {code.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <button 
                              className="btn btn-secondary"
                              onClick={() => handleEdit(code)}
                              style={{ marginRight: '0.5rem' }}
                            >
                              Edit
                            </button>
                            <button 
                              className="btn btn-warning"
                              onClick={() => handleResetUsage(code.id, code.code)}
                              style={{ marginRight: '0.5rem' }}
                              title="Reset all usage records for this code"
                            >
                              Reset Usage
                            </button>
                            <button 
                              className="btn btn-danger"
                              onClick={() => handleDelete(code.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Usage Statistics Tab */}
          {activeTab === 'usage' && (
            <div className="usage-tab">
              <h2>Usage Statistics</h2>
              
              {usageStats.length === 0 ? (
                <div className="no-articles">
                  <p>No usage statistics available yet.</p>
                </div>
              ) : (
                <div className="usage-table">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>User</th>
                        <th>Original Amount</th>
                        <th>Discount Amount</th>
                        <th>Final Amount</th>
                        <th>Used At</th>
                        <th>Stripe Session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageStats.map(usage => (
                        <tr key={usage.id}>
                          <td><strong>{usage.discount_code}</strong></td>
                          <td>{usage.user_name}</td>
                          <td>{formatCurrency(usage.original_amount)}</td>
                          <td style={{ color: '#28a745' }}>
                            -{formatCurrency(usage.discount_amount)}
                          </td>
                          <td><strong>{formatCurrency(usage.final_amount)}</strong></td>
                          <td>{new Date(usage.used_at).toLocaleDateString()}</td>
                          <td>
                            {usage.stripe_session_id && (
                              <small style={{ color: '#666' }}>
                                {usage.stripe_session_id.substring(0, 20)}...
                              </small>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <style jsx>{`
        .discount-codes-section {
          padding: 2rem;
        }

        .tab-navigation {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .tab-btn {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }

        .tab-btn.active {
          border-bottom-color: #007bff;
          color: #007bff;
          font-weight: 600;
        }

        .tab-btn:hover {
          background-color: #f8f9fa;
        }

        .codes-table,
        .usage-table {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .admin-table {
          width: 100%;
          border-collapse: collapse;
        }

        .admin-table th,
        .admin-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }

        .admin-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #2d3748;
        }

        .plan-selection {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .status {
          padding: 0.25rem 0.75rem;
          border-radius: 2rem;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .status.published {
          background-color: #d4edda;
          color: #155724;
        }

        .status.draft {
          background-color: #f8d7da;
          color: #721c24;
        }
      `}</style>
    </div>
  );
}

// Article Requests Page Component  
function ArticleRequestsPage({ user, handleLogout, handleApiAuthFailure }) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    language: '',
    status: ''
  });

  // Fetch article requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        handleApiAuthFailure();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/article-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        console.error('Error fetching requests:', response.status);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter requests based on filter state
  const filteredRequests = requests.filter(request => {
    const matchesSearch = !filters.search || 
      request.topic.toLowerCase().includes(filters.search.toLowerCase()) ||
      request.user_name.toLowerCase().includes(filters.search.toLowerCase()) ||
      request.user_email.toLowerCase().includes(filters.search.toLowerCase()) ||
      (request.description && request.description.toLowerCase().includes(filters.search.toLowerCase()));
    
    const matchesCategory = !filters.category || request.profession_category === filters.category;
    const matchesLanguage = !filters.language || request.language === filters.language;
    const matchesStatus = !filters.status || request.status === filters.status;
    
    return matchesSearch && matchesCategory && matchesLanguage && matchesStatus;
  });

  // Update filter
  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      language: '',
      status: ''
    });
  };

  // Get unique values for filter options
  const getUniqueValues = (key) => {
    const values = requests.map(request => request[key]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  // Update request status
  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [requestId]: true }));
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${API_BASE_URL}/admin/article-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Update the local state
        setRequests(prev => prev.map(request => 
          request.id === requestId 
            ? { ...request, status: newStatus }
            : request
        ));
      } else if (response.status === 401 || response.status === 403) {
        handleApiAuthFailure();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating request status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [requestId]: false }));
    }
  };

  // Get status badge styling
  const getStatusStyling = (status) => {
    const styles = {
      pending: { background: '#ffc107', color: 'white' },
      approved: { background: '#28a745', color: 'white' },
      rejected: { background: '#dc3545', color: 'white' },
      in_progress: { background: '#17a2b8', color: 'white' },
      completed: { background: '#6f42c1', color: 'white' }
    };
    return styles[status] || { background: '#6c757d', color: 'white' };
  };

  // Load requests on component mount
  useEffect(() => {
    fetchRequests();
  }, []);

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-brand" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
          <h2>Protagon - Admin Dashboard</h2>
        </div>
        <div className="nav-content">
          <div className="nav-info">
            <span>Welcome, {user.first_name} {user.last_name}</span>
          </div>
          <button className="btn btn-secondary logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </nav>

      <main className="main-content">
        <div className="admin-header">
          <div className="admin-nav">
            <button className="nav-tab" onClick={() => navigate('/dashboard')}>
              📚 Articles Management
            </button>
            <button className="nav-tab" onClick={() => navigate('/news-management')}>
              📰 News Management
            </button>
            <button className="nav-tab" onClick={() => navigate('/feedback-management')}>
              📝 Feedback Management
            </button>
            <button className="nav-tab" onClick={() => navigate('/discount-codes')}>
              🎟️ Discount Codes
            </button>
            <button className="nav-tab active">
              📝 Article Requests
            </button>
          </div>
        </div>

        <div className="article-requests-section">
          <div className="requests-header">
            <h2>Article Requests Management</h2>
            <p>Review and manage user requests for new articles and sources</p>
          </div>

          {loading ? (
            <div className="loading-state">Loading article requests...</div>
          ) : (
            <div className="requests-table">
              <div className="table-header">
                <h3>User Requests ({filteredRequests.length}{requests.length !== filteredRequests.length ? ` of ${requests.length}` : ''})</h3>
                
                {/* Search and Filter Controls */}
                <div className="filters-section">
                  <div className="filters-row">
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder="Search by topic, user name, email or description..."
                        value={filters.search}
                        onChange={(e) => updateFilter('search', e.target.value)}
                        className="search-input"
                      />
                      <span className="search-icon">🔍</span>
                    </div>
                    
                    <div className="filter-controls">
                      <select
                        value={filters.category}
                        onChange={(e) => updateFilter('category', e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Categories</option>
                        {PROFESSION_CATEGORIES.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                      
                      <select
                        value={filters.language}
                        onChange={(e) => updateFilter('language', e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Languages</option>
                        {LANGUAGES.map(language => (
                          <option key={language} value={language}>{language}</option>
                        ))}
                      </select>
                      
                      <select
                        value={filters.status}
                        onChange={(e) => updateFilter('status', e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      
                      {(filters.search || filters.category || filters.language || filters.status) && (
                        <button onClick={clearFilters} className="clear-filters-btn">
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {filteredRequests.length === 0 ? (
                <div className="no-requests">
                  {requests.length === 0 ? (
                    <>
                      <p>No article requests have been submitted yet.</p>
                      <p>Users can request articles from their account management page.</p>
                    </>
                  ) : (
                    <>
                      <p>No requests match your current filters.</p>
                      <button onClick={clearFilters} className="clear-filters-btn">
                        Clear Filters
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Topic</th>
                      <th>Category</th>
                      <th>Language</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((request) => (
                      <tr key={request.id}>
                        <td>
                          <div>
                            <strong>{request.user_name}</strong>
                            <br />
                            <small style={{ color: '#666' }}>{request.user_email}</small>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{request.topic}</strong>
                            {request.description && (
                              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                                {request.description.length > 100 
                                  ? `${request.description.substring(0, 100)}...` 
                                  : request.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{request.profession_category}</td>
                        <td>{request.language}</td>
                        <td>
                          <span style={{
                            ...getStatusStyling(request.status),
                            padding: '0.25rem 0.75rem',
                            borderRadius: '1rem',
                            fontSize: '0.8rem',
                            textTransform: 'capitalize'
                          }}>
                            {request.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>{new Date(request.created_at).toLocaleDateString()}</td>
                        <td>
                          <select
                            value={request.status}
                            onChange={(e) => updateRequestStatus(request.id, e.target.value)}
                            disabled={updatingStatus[request.id]}
                            style={{
                              padding: '0.25rem 0.5rem',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              minWidth: '120px'
                            }}
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="rejected">Rejected</option>
                          </select>
                          {updatingStatus[request.id] && (
                            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                              Updating...
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </main>

      <style jsx>{`
        .article-requests-section {
          padding: 2rem;
        }
        .requests-header h2 {
          color: #2d3748;
          margin-bottom: 0.5rem;
        }
        .requests-header p {
          color: #718096;
        }
        .requests-table {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .table-header {
          padding: 1.5rem;
          background-color: #f8f9fa;
          border-bottom: 1px solid #e2e8f0;
        }
        .table-header h3 {
          margin: 0 0 1rem 0;
          font-weight: 600;
          color: #2d3748;
        }
        .filters-section {
          margin-top: 1rem;
        }
        .filters-row {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .search-box {
          position: relative;
          flex: 1;
          max-width: 500px;
        }
        .search-input {
          width: 100%;
          padding: 0.75rem 2.5rem 0.75rem 1rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.9rem;
          background: white;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .search-input:focus {
          outline: none;
          border-color: #3182ce;
          box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
        }
        .search-icon {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #a0aec0;
          pointer-events: none;
        }
        .filter-controls {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
        }
        .filter-select {
          padding: 0.5rem 0.75rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.85rem;
          background: white;
          min-width: 140px;
          transition: border-color 0.2s;
        }
        .filter-select:focus {
          outline: none;
          border-color: #3182ce;
        }
        .clear-filters-btn {
          padding: 0.5rem 1rem;
          background: #e53e3e;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.85rem;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .clear-filters-btn:hover {
          background: #c53030;
        }
        .no-requests {
          padding: 3rem;
          text-align: center;
          color: #718096;
        }
        .loading-state {
          padding: 3rem;
          text-align: center;
          color: #718096;
        }
        .requests-table .admin-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        .requests-table .admin-table th,
        .requests-table .admin-table td {
          padding: 1rem 1.5rem;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }
        .requests-table .admin-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #2d3748;
          border-bottom: 2px solid #e2e8f0;
        }
        .requests-table .admin-table td {
          vertical-align: top;
        }
        .requests-table .admin-table tr:hover {
          background-color: #f7fafc;
        }

        @media (min-width: 768px) {
          .filters-row {
            flex-direction: row;
            align-items: flex-start;
          }
          .search-box {
            flex: 1;
          }
          .filter-controls {
            flex-shrink: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default App;

